import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Tooltip
} from '@mui/material';
import {
  LocalShipping,
  AttachMoney,
  Schedule,
  Map,
  Refresh,
  Place,
  CalendarToday,
  Search,
  Scale,
  Balance
} from '@mui/icons-material';
import GavelIcon from '@mui/icons-material/Gavel';

import { styled } from '@mui/material/styles';
import { OpenInFull, Close as CloseIcon } from '@mui/icons-material';

import ReactApexChart from 'react-apexcharts';
import { GetBookedShipments } from '../../apis/Agency';
import { useDispatch, useSelector } from 'react-redux';
import BubbleMaps from './BuubleMapChart';
import { MetaTags } from 'react-meta-tags';
import OriginDestinationTab from './OriginDestinationTab';
import OnTimeDeliveryTab from './OnTimeDeliveryTab';
import WeeklyTrendsTab from './WeeklyTrendsTab';
import { margin } from '@mui/system';
import ClaimsManagementModule from '../CarrierClaimForm/ClaimsManagementModule';
import ClaimsDashboard from '../CarrierClaimForm/ClaimsDashboard';
import moment from 'moment-timezone';

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  minHeight: '60vh',
  maxHeight: '90vh',
  padding: theme.spacing(1.5),
  paddingBottom: '1px',
  overflow: 'auto'
}));

const StatCard = styled(Card)(({ theme, color = 'primary' }) => ({
  height: '124px', // Your desired height
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center', // This vertically centers content
  alignItems: 'center', // This horizontally centers content
  textAlign: 'center', // Ensures text alignment
  background: `linear-gradient(135deg, ${theme.palette[color].main} 0%, ${theme.palette[color].dark} 100%)`,
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-40%',
    right: '-40%',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%'
  },
  // Additional styling for content alignment
  '& .MuiCardContent-root': {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1
  }
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  height: '270px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '270px'
  // width: '500px'
}));

const CompactTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '28px',
  display: 'flex',
  '& .MuiTab-root': {
    minHeight: '48px',
    padding: '8px 12px',
    fontSize: '0.70rem',
    fontWeight: 500,
    '& .MuiTab-iconWrapper': {
      marginBottom: '2px',
      fontSize: '18px'
    }
  }
}));

const TARGET_TIMEZONE = 'America/New_York';

// Consistent date parsing function
const parseToESTDate = dateString => {
  if (!dateString) return null;
  return moment.tz(dateString, TARGET_TIMEZONE).startOf('day').toDate();
};

// Consistent comparison function
const isDateInRangeEST = (dateString, startDate, endDate) => {
  const date = parseToESTDate(dateString);
  const start = parseToESTDate(startDate);
  const end = parseToESTDate(endDate);

  if (!date || !start || !end) return false;
  return date >= start && date <= end;
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    id={`dashboard-tabpanel-${index}`}
    aria-labelledby={`dashboard-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 1, display: 'flex' }}>{children}</Box>}
  </div>
);

// Searchable Select Component
const SearchableSelect = ({ label, value, options, onChange, sx = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const filteredOptions = options.filter(option =>
    String(option).toLowerCase().includes(searchTerm.toLowerCase())
  );
  const highlight = (text, query) => {
    if (!query) return text;

    const parts = String(text).split(new RegExp(`(${query})`, 'gi'));

    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span
          key={index}
          style={{ fontWeight: 600, backgroundColor: '#fff3b0' }}
        >
          {part}
        </span>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  return (
    <FormControl size='small' sx={{ minWidth: 100, ...sx }}>
      <InputLabel sx={{ width: '220px' }}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={onChange}
        sx={{ fontSize: '0.75rem' }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
              maxWidth: label === 'Customer' ? 260 : 130
            }
          }
        }}
      >
        <MenuItem sx={{ p: 0, fontSize: '0.75rem' }}>
          <TextField
            size='small'
            placeholder={`Search ...`}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              )
            }}
            sx={{
              // p: 1,
              fontSize: '0.75rem',
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </MenuItem>
        <MenuItem value='All' sx={{ fontSize: '0.75rem' }}>
          All
        </MenuItem>
        {searchTerm && filteredOptions.length > 0 && (
          <MenuItem disabled sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
            Found {filteredOptions.length}{' '}
            {filteredOptions.length === 1 ? 'item' : 'items'}
          </MenuItem>
        )}

        {filteredOptions.length === 0 ? (
          <MenuItem disabled sx={{ opacity: 0.7, fontStyle: 'italic' }}>
            No results found
          </MenuItem>
        ) : (
          filteredOptions.map(option => (
            <MenuItem key={option} value={option} sx={{ fontSize: '0.75rem' }}>
              {highlight(option, searchTerm)}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

const TMSDashboard = () => {
  const AgencyID = useSelector(state => state.userDetails.agencyLoginID);
  const [activeTab, setActiveTab] = useState(0);
  const [shipsData, setShipsData] = useState([]);
  const [expandedChart, setExpandedChart] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [notBookedShipments, setNotBookedShipments] = useState([]);
  const [filters, setFilters] = useState({
    customer: 'All',
    originState: 'All',
    destinationState: 'All',
    shipmentType: 'All',
    shipmentTypeRule: 'All',
    carrier: 'All',
    dateRange: 'custom',
    startDate: new Date('2024-10-28').toISOString().split('T')[0],
    endDate: new Date('2026-01-21').toISOString().split('T')[0],
    week: 'All',
    // New filter states for chart clicks
    selectedMonth: 'All',
    selectedWeightRange: 'All',
    selectedMileRange: 'All',
    selectedCarrier: 'All',
    selectedShipmentType: 'All'
  });
  const [userPermissionsArr, setUserPermissionsArr] = useState(null);

  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const userRolePermissions = useSelector(
    state => state?.userDetails?.userRolePermissionsArr
  );

  useEffect(() => {
    const filteredCarrierPermissions = userRolePermissions?.filter(
      items => items.permissionName === 'Dashboard'
    );
    setUserPermissionsArr(filteredCarrierPermissions);
  }, [userRolePermissions]);
  const UserIsAdmin = useSelector(state => state?.userDetails?.isAdmin);

  useEffect(() => {
    GetBookedShipments(AgencyID)
      .then(res => {
        if (res.status === 200) {
          const allowedStatuses = ['InTransit', 'Delivered'];

          // list the keys you want normalized (expand as needed)
          const COMPANY_KEYS = [
            'carrierName',
            'leastCostCarrierName',
            'leastTransitCarrierName',
            'tlCarrierName',
            'originCompany',
            'destinationCompany',
            'billToCompany',
            'carrierBillToCompany',
            'carrierBillToCompany'
            // add any other string fields you want normalized
          ];

          function smartNormalizeName(raw) {
            if (raw === null || raw === undefined) return raw;
            // convert to string and collapse whitespace
            let s = String(raw).replace(/\s+/g, ' ').trim();

            // remove leading/trailing punctuation (but keep internal punctuation like & and -)
            s = s.replace(/^[\s\p{P}]+|[\s\p{P}]+$/gu, ''); // uses unicode-aware punctuation class

            if (!s) return s;

            // split on spaces, normalize each token
            const tokens = s.split(' ').map(token => {
              // keep digits & & - / . as part of token
              const cleaned = token.replace(
                /^[^\p{L}\p{N}&-/.]+|[^\p{L}\p{N}&-/.]+$/gu,
                ''
              );

              // if token is already all uppercase and short (likely an acronym) keep it uppercase
              if (
                cleaned &&
                cleaned === cleaned.toUpperCase() &&
                cleaned.length <= 4
              ) {
                return cleaned;
              }

              // if token contains a dot (e.g., "e.g." or "mr.") or slash, keep original-ish but normalize case
              if (cleaned.includes('.') || cleaned.includes('/')) {
                return cleaned
                  .split(/([./])/)
                  .map(part => {
                    if (part === '.' || part === '/') return part;
                    return (
                      part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
                    );
                  })
                  .join('');
              }

              // title-case the token but keep internal hyphen/apos parts proper (O'Neil, Co-op)
              const subParts = cleaned.split(/([-'])/); // keep separators
              return subParts
                .map(sp => {
                  if (sp === '-' || sp === "'") return sp;
                  return sp.length === 0
                    ? sp
                    : sp.charAt(0).toUpperCase() + sp.slice(1).toLowerCase();
                })
                .join('');
            });

            // rejoin tokens and also collapse any accidental multi-spaces again
            return tokens.join(' ').replace(/\s+/g, ' ').trim();
          }

          // Use this on your API response
          const normalizeCarrierName = carrierName => {
            if (!carrierName) return carrierName;

            // Convert to lowercase and trim
            const name = carrierName.toString().toLowerCase().trim();

            // Handle XPO variations
            if (name.includes('xpo')) {
              // Return standardized XPO name
              return 'XPO'; // or 'XPO Logistics' based on your preference
            }

            // Return original name with smart normalization
            return smartNormalizeName(carrierName);
          };

          const filteredShips = res.data.data
            .filter(shipment =>
              allowedStatuses.includes(shipment.shipmentStatus)
            )
            .map(shipment => {
              // create shallow copy (immutable)
              const copy = { ...shipment };

              // normalize each relevant key if present
              COMPANY_KEYS.forEach(key => {
                if (
                  Object.prototype.hasOwnProperty.call(copy, key) &&
                  copy[key] != null
                ) {
                  if (
                    key === 'CarrierName' ||
                    key === 'carrierName' ||
                    key === 'carrier'
                  ) {
                    // Use special normalization for carrier names
                    copy[key] = normalizeCarrierName(copy[key]);
                  } else {
                    copy[key] = smartNormalizeName(copy[key]);
                  }
                }
              });

              return copy;
            });

          const notBookedShips = res.data.data
            .filter(shipment => shipment.shipmentStatus !== 'Booked')
            .map(shipment => {
              // create shallow copy (immutable)
              const copy = { ...shipment };

              // normalize each relevant key if present
              COMPANY_KEYS.forEach(key => {
                if (
                  Object.prototype.hasOwnProperty.call(copy, key) &&
                  copy[key] != null
                ) {
                  copy[key] = smartNormalizeName(copy[key]);
                }
              });

              return copy;
            });

          // set state
          setNotBookedShipments(notBookedShips);
          setShipsData(filteredShips);
          setShipments(filteredShips);

          // console.log(
          //   filteredShips.filter(data => data.shipmentStatus data.carrierId === 2).length,
          //   'fedex carrier'
          // );

          // dispatch(setDashboardDataArray(res.data.data));
          console.log('Booked Shipments Data: ', res.data.data);
        }
      })
      .catch(err => console.log('Error in Booked Shipments: ', err))
      .finally(() => setLoading(false));
  }, [AgencyID]);

  const createDateWithoutTime = dateString => {
    if (!dateString) return null;

    // Use moment-timezone to parse in EST timezone consistently
    const m = moment.tz(dateString, 'America/New_York');

    if (!m.isValid()) return null;

    // Return Date object at start of day in EST
    return m.startOf('day').toDate();
  };

  // Enhanced filter handler with chart click support
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);

    const shipsDataCopy = [...shipsData];

    let filteredData = shipsData;

    // Apply all basic filters
    if (newFilters.customer !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.destinationCompany === newFilters.customer
      );
    }

    if (newFilters.originState !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.originState === newFilters.originState
      );
    }

    if (newFilters.destinationState !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.destinationState === newFilters.destinationState
      );
    }

    if (newFilters.shipmentType !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.shipmentType === newFilters.shipmentType
      );
    }

    if (newFilters.shipmentTypeRule !== 'All') {
      filteredData = filteredData.filter(
        shipment =>
          shipment.shipmentTypeRule.trim().toLowerCase() ===
          newFilters.shipmentTypeRule.toLowerCase()
      );
    }

    if (newFilters.carrier !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.carrierName === newFilters.carrier
      );
    }

    // Apply chart click filters
    if (newFilters.selectedMonth !== 'All') {
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const [monthAbbr, yearSuffix] = newFilters.selectedMonth.split('-');
      const monthIndex = monthNames.indexOf(monthAbbr);
      const year = 2000 + parseInt(yearSuffix);

      if (monthIndex !== -1) {
        filteredData = filteredData.filter(shipment => {
          if (!shipment.actualPickupDate) return false;
          const date = new Date(shipment.actualPickupDate);
          return date.getMonth() === monthIndex && date.getFullYear() === year;
        });
      }
    }

    if (newFilters.selectedWeightRange !== 'All') {
      const weightRanges = {
        '<500': { min: 0, max: 499 },
        '500-999': { min: 500, max: 999 },
        '1,000-1,999': { min: 1000, max: 1999 },
        '2,000-4,999': { min: 2000, max: 4999 },
        '5,000-9,999': { min: 5000, max: 9999 },
        '10,000-19,999': { min: 10000, max: 19999 },
        '20,000-40,000': { min: 20000, max: 40000 },
        '>40,000': { min: 40001, max: Infinity }
      };

      const range = weightRanges[newFilters.selectedWeightRange];
      if (range) {
        filteredData = filteredData.filter(shipment => {
          const weight = shipment.totalGrossWeight || 0;
          return weight >= range.min && weight <= range.max;
        });
      }
    }

    if (newFilters.selectedMileRange !== 'All') {
      const mileRanges = {
        '0-49': { min: 0, max: 49 },
        '50-99': { min: 50, max: 99 },
        '100-249': { min: 100, max: 249 },
        '250-499': { min: 250, max: 499 },
        '500-749': { min: 500, max: 749 },
        '750-999': { min: 750, max: 999 },
        '1,000-1,499': { min: 1000, max: 1499 },
        '1,500-1,999': { min: 1500, max: 1999 },
        '2,000-2,499': { min: 2000, max: 2499 },
        '2,500+': { min: 2500, max: Infinity }
      };

      const range = mileRanges[newFilters.selectedMileRange];
      if (range) {
        filteredData = filteredData.filter(shipment => {
          const METERS_PER_MILE = 1609.344;
          const miles = shipment.shipmentMiles
            ? Math.round(shipment.shipmentMiles / METERS_PER_MILE)
            : 0;
          return miles >= range.min && miles <= range.max;
        });
      }
    }

    if (newFilters.selectedCarrier !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.carrierName === newFilters.selectedCarrier
      );
    }

    if (newFilters.selectedShipmentType !== 'All') {
      filteredData = filteredData.filter(
        shipment => shipment.shipmentType === newFilters.selectedShipmentType
      );
    }

    // Date Range Filter Logic
    // Date Range Filter Logic
    if (newFilters.dateRange !== 'All') {
      switch (newFilters.dateRange) {
        case 'last7':
          const sevenDaysAgo = moment()
            .tz('America/New_York')
            .subtract(7, 'days')
            .startOf('day')
            .toDate();

          filteredData = filteredData.filter(shipment => {
            if (!shipment.actualDeliveryDate) return false;
            const shipmentMoment = moment
              .tz(shipment.actualDeliveryDate, 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= sevenDaysAgo;
          });
          break;

        case 'last30':
          const thirtyDaysAgo = moment()
            .tz('America/New_York')
            .subtract(30, 'days')
            .startOf('day')
            .toDate();

          filteredData = filteredData.filter(shipment => {
            if (!shipment.actualDeliveryDate) return false;
            const shipmentMoment = moment
              .tz(shipment.actualDeliveryDate, 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= thirtyDaysAgo;
          });
          break;

        case 'last90':
          const ninetyDaysAgo = moment()
            .tz('America/New_York')
            .subtract(90, 'days')
            .startOf('day')
            .toDate();

          filteredData = filteredData.filter(shipment => {
            if (!shipment.actualDeliveryDate) return false;
            const shipmentMoment = moment
              .tz(shipment.actualDeliveryDate, 'America/New_York')
              .startOf('day');
            if (!shipmentMoment.isValid()) return false;

            const shipmentDate = shipmentMoment.toDate();
            return shipmentDate && shipmentDate >= ninetyDaysAgo;
          });
          break;

        case 'custom':
          if (newFilters.startDate && newFilters.endDate) {
            const startMoment = moment
              .tz(newFilters.startDate, 'America/New_York')
              .startOf('day');
            const endMoment = moment
              .tz(newFilters.endDate, 'America/New_York')
              .endOf('day');

            if (startMoment.isValid() && endMoment.isValid()) {
              const startDate = startMoment.toDate();
              const endDate = endMoment.toDate();

              filteredData = filteredData.filter(shipment => {
                if (!shipment.actualDeliveryDate) return false;

                const shipmentMoment = moment
                  .tz(shipment.actualDeliveryDate, 'America/New_York')
                  .startOf('day');
                if (!shipmentMoment.isValid()) return false;

                const shipmentDate = shipmentMoment.toDate();

                return shipmentDate >= startDate && shipmentDate <= endDate;
              });
            }
          }
          break;

        default:
          // Specific date selected (not 'custom')
          if (newFilters.dateRange !== 'custom') {
            const selectedDateMoment = moment
              .tz(newFilters.dateRange, 'America/New_York')
              .startOf('day');
            if (selectedDateMoment.isValid()) {
              const selectedDate = selectedDateMoment.toDate();

              filteredData = filteredData.filter(shipment => {
                if (!shipment.actualDeliveryDate) return false;
                const shipmentMoment = moment
                  .tz(shipment.actualDeliveryDate, 'America/New_York')
                  .startOf('day');
                if (!shipmentMoment.isValid()) return false;

                const shipmentDate = shipmentMoment.toDate();
                return (
                  shipmentDate &&
                  shipmentDate.getTime() === selectedDate.getTime()
                );
              });
            }
          }
          break;
      }
    }

    // Week Filter Logic
    if (newFilters.week !== 'All') {
      filteredData = filteredData.filter(shipment => {
        if (!shipment.actualDeliveryDate) return false;

        const shipmentDate = createDateWithoutTime(shipment.actualDeliveryDate);
        if (!shipmentDate) return false;

        // Extract week number from your week format "2024 W44"
        const [year, weekNum] = newFilters.week.split(' W');

        // Get ISO week number
        const getISOWeekNumber = date => {
          const target = new Date(date);
          const dayNumber = (target.getUTCDay() + 6) % 7; // Make Monday=0
          target.setUTCDate(target.getUTCDate() - dayNumber + 3);
          const firstThursday = target.valueOf();
          target.setUTCMonth(0, 1);
          if (target.getUTCDay() !== 4) {
            target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7));
          }
          return 1 + Math.ceil((firstThursday - target) / 604800000);
        };

        const shipmentWeek = getISOWeekNumber(shipmentDate);
        const shipmentYear = shipmentDate.getUTCFullYear();

        return (
          shipmentYear.toString() === year &&
          shipmentWeek.toString() === weekNum
        );
      });
    }

    setShipments(filteredData);
  };

  // Chart click handlers
  const handleMonthFilter = selectedMonth => {
    handleFilterChange('selectedMonth', selectedMonth);
    if (expandedChart) setExpandedChart(null);
  };

  const handleCarrierFilter = selectedCarrier => {
    handleFilterChange('selectedCarrier', selectedCarrier);
    if (expandedChart) setExpandedChart(null);
  };

  const handleWeightFilter = selectedWeightRange => {
    handleFilterChange('selectedWeightRange', selectedWeightRange);
    if (expandedChart) setExpandedChart(null);
  };

  const handleMileFilter = selectedMileRange => {
    handleFilterChange('selectedMileRange', selectedMileRange);
    if (expandedChart) setExpandedChart(null);
  };

  const handleShipmentTypeFilter = selectedShipmentType => {
    handleFilterChange('selectedShipmentType', selectedShipmentType);
    if (expandedChart) setExpandedChart(null);
  };

  const getUniqueWeeks = () => {
    const getWeekNumber = date => {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    };

    const weeks = shipsData
      .map(shipment => {
        if (!shipment.actualPickupDate) return null;
        const date = new Date(shipment.actualPickupDate);
        const year = date.getFullYear();
        const week = getWeekNumber(date);
        return `${year} W${week}`;
      })
      .filter(Boolean);

    return ['All', ...new Set(weeks)];
  };

  // Add this optimized data calculation before your return statement
  const monthlyShipmentTypeData = React.useMemo(() => {
    const dataByMonthType = {};
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.shipmentType) return;

      const date = new Date(shipment.actualPickupDate);

      // FIXED: Use consistent month formatting
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`; // Format: Oct-24, Nov-24

      if (!dataByMonthType[monthYear]) {
        dataByMonthType[monthYear] = { LTL: 0, TL: 0, Parcel: 0 };
      }

      const type = shipment.shipmentType;
      if (dataByMonthType[monthYear][type] !== undefined) {
        dataByMonthType[monthYear][type] += shipment.quoteNetCharge || 0;
      }
    });

    // Keep the fixed sorting logic
    const allMonths = Object.keys(dataByMonthType).sort((a, b) => {
      const parseMonthYear = monthYear => {
        const [monthAbbr, yearSuffix] = monthYear.split('-');
        const fullYear = 2000 + parseInt(yearSuffix, 10);

        const monthIndex = monthNames.indexOf(monthAbbr);
        if (monthIndex === -1) return 0;

        return fullYear * 12 + monthIndex;
      };

      return parseMonthYear(a) - parseMonthYear(b);
    });

    const ltlData = allMonths.map(month =>
      Math.round(dataByMonthType[month]?.LTL || 0)
    );
    const tlData = allMonths.map(month =>
      Math.round(dataByMonthType[month]?.TL || 0)
    );

    const parcelData = allMonths.map(month =>
      Math.round(dataByMonthType[month]?.Parcel || 0)
    );

    // Dynamic series based on active shipment type filter
    const getSeriesData = () => {
      const series = [];
      const currentFilter = filters.shipmentType;
      const includeAll = currentFilter === 'All';

      if (includeAll || currentFilter === 'LTL') {
        series.push({ name: 'LTL', data: ltlData });
      }
      if (includeAll || currentFilter === 'Parcel') {
        series.push({ name: 'Parcel', data: parcelData });
      }

      if (includeAll || currentFilter === 'TL') {
        series.push({ name: 'TL', data: tlData });
      }

      return series;
    };

    return {
      categories: allMonths,
      series: getSeriesData()
    };
  }, [shipments, filters.shipmentType]);

  const weeks = getUniqueWeeks();

  // Update resetFilters function to include new filter states
  const resetFilters = () => {
    setFilters({
      customer: 'All',
      originState: 'All',
      destinationState: 'All',
      shipmentType: 'All',
      shipmentTypeRule: 'All',
      carrier: 'All',
      dateRange: 'custom',
      startDate: new Date('2024-10-28').toISOString().split('T')[0],
      endDate: new Date('2026-01-21').toISOString().split('T')[0],
      week: 'All',
      selectedMonth: 'All',
      selectedWeightRange: 'All',
      selectedMileRange: 'All',
      selectedCarrier: 'All',
      selectedShipmentType: 'All'
    });
    setShipments(shipsData);
  };

  // Calculate statistics
  const totalFreightSpent = shipments.reduce(
    (sum, shipment) => sum + (shipment.quoteNetCharge || 0),
    0
  );

  // If you want to ensure it doesn't go negative
  const netFreightSpent = Math.max(0, totalFreightSpent);

  const totalShipments = shipments.length;

  // Additional charts data
  const capitalize = str =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  // Get unique values for filters
  const customers = [
    'All',
    ...new Set(shipsData.map(s => s.destinationCompany).filter(Boolean))
  ];
  const originStates = [
    'All',
    ...new Set(shipsData.map(s => s.originState).filter(Boolean))
  ];
  const destinationStates = [
    'All',
    ...new Set(shipsData.map(s => s.destinationState).filter(Boolean))
  ];
  const shipmentTypes = [
    'All',
    ...new Set(shipsData.map(s => s.shipmentType).filter(Boolean))
  ];
  const shipmentModes = [
    'All',
    ...[
      ...new Set(
        shipsData
          .map(s => s.shipmentTypeRule)
          .filter(Boolean)
          .map(v => String(v).trim().toLowerCase())
      )
    ].map(capitalize)
  ];

  const carriers = [
    'All',
    ...new Set(shipsData.map(s => s.carrierName).filter(Boolean))
  ];

  const freightByShipMode = shipments.reduce((acc, shipment) => {
    if (shipment.shipmentTypeRule) {
      // Normalize for matching
      const raw = String(shipment.shipmentTypeRule).trim().toLowerCase();
      const mode = capitalize(raw); // Final clean key

      if (!acc[mode]) {
        acc[mode] = 0;
      }

      acc[mode] += shipment.quoteNetCharge || 0;
    }
    return acc;
  }, {});

  if (Object.keys(freightByShipMode).length === 0) {
    freightByShipMode['Outbound'] = 0;
    freightByShipMode['Inbound'] = 0;
    freightByShipMode['Transfer'] = 0;
    freightByShipMode['Dropship'] = 0;
  }

  const monthlyFreight = shipments.reduce((acc, shipment) => {
    if (shipment.actualPickupDate) {
      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
      const monthYear = `${month}-${year}`; // Format: Oct-24, Nov-24

      if (!acc[monthYear]) {
        acc[monthYear] = 0;
      }
      acc[monthYear] += shipment.quoteNetCharge || 0;
    }
    return acc;
  }, {});

  const monthlyData = Object.entries(monthlyFreight)
    .map(([monthYear, freight]) => {
      const [monthAbbr, year] = monthYear.split('-');
      const date = new Date(`01 ${monthAbbr} 20${year}`);
      return { month: monthYear, freight, date };
    })
    .sort((a, b) => a.date - b.date)
    .map(({ month, freight }) => ({ month, freight }));

  const shipmentCountsByType = shipments.reduce((acc, shipment) => {
    if (shipment.shipmentTypeRule) {
      const raw = String(shipment.shipmentTypeRule).trim().toLowerCase();
      const mode = capitalize(raw);

      if (!acc[mode]) {
        acc[mode] = 0;
      }
      acc[mode] += 1;
    }
    return acc;
  }, {});

  // 3. Fill with zeros if empty
  if (Object.keys(freightByShipMode).length === 0) {
    freightByShipMode['Outbound'] = 0;
    freightByShipMode['Inbound'] = 0;
    freightByShipMode['Transfer'] = 0;
    freightByShipMode['Dropship'] = 0;

    shipmentCountsByType['Outbound'] = 0;
    shipmentCountsByType['Inbound'] = 0;
    shipmentCountsByType['Transfer'] = 0;
    shipmentCountsByType['Dropship'] = 0;
  }

  // 4. Create pie chart series data (FREIGHT AMOUNTS)
  const shipTypePieSeries = Object.values(freightByShipMode);

  // 5. Pie chart options
  const shipTypePieOptions = {
    chart: {
      type: 'donut',
      height: expandedChart === 'shipmentTypePie' ? 200 : 300
    },
    labels: Object.keys(freightByShipMode),
    colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560'],
    legend: {
      position: expandedChart === 'shipmentTypePie' ? 'top' : 'bottom'
      // itemMargin: {
      //   horizontal: 20,
      //   vertical: -10
      // }
    },
    plotOptions: {
      pie: {
        donut: {
          size: expandedChart === 'shipmentTypePie' ? '70%' : '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 'bold',
              offsetY: -5
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
              offsetY: 10,
              formatter: function (val) {
                const inThousands = val / 1000;
                return '$' + Math.round(inThousands).toLocaleString() + 'K';
              }
            },
            total: {
              show: false
            }
          }
        },
        expandOnClick: false,
        dataLabels: {
          offset: expandedChart === 'shipmentTypePie' ? 68 : 27,
          minAngleToShowLabel: 0
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, { seriesIndex, w }) {
        const value = w.config.series[seriesIndex];
        const inThousands = value / 1000;

        if (value === 0) return '$0';

        return '$' + Math.round(inThousands).toLocaleString() + 'K';
      },
      style: {
        fontSize: expandedChart === 'shipmentTypePie' ? '13px' : '11px',
        fontWeight: 'bold',
        colors: ['#1a237e']
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        padding: expandedChart === 'shipmentTypePie' ? 2 : 4,
        borderRadius: 2,
        borderWidth: 0,
        opacity: 0.9
      },
      dropShadow: {
        enabled: false
      }
    },
    stroke: {
      show: true,
      width: 3
    },
    tooltip: {
      y: {
        formatter: function (val, { seriesIndex, w }) {
          const totalFreight = Object.values(freightByShipMode).reduce(
            (a, b) => a + b,
            0
          );
          const percentage =
            totalFreight > 0 ? ((val / totalFreight) * 100).toFixed(1) : '0';

          return `Freight Spent: $${val.toLocaleString()} (${percentage}%)<br/>`;
        }
      }
    }
  };

  // Format function (only millions, no thousands)
  const totalPoundsMoved = shipments.reduce(
    (sum, item) => sum + (item.totalNetWeight || 0),
    0
  );

  const costPerPound = netFreightSpent / totalPoundsMoved;

  // Format function (only millions, no thousands)
  const formatToMillions = (
    value,
    isCurrency = false,
    isTotalFreightSpend = false
  ) => {
    if (value == null || isNaN(value)) return isCurrency ? '$0' : '0';

    // 🔹 SPECIAL LOGIC — Total Freight Spend ONLY
    if (isTotalFreightSpend) {
      if (value >= 1_000_000) {
        const rounded = Math.round(value / 1_000_000);
        return isCurrency ? `$${rounded}M` : `${rounded}M`;
      }

      if (value >= 1_000) {
        const rounded = Math.round(value / 1_000);
        return isCurrency ? `$${rounded}K` : `${rounded}K`;
      }

      return isCurrency
        ? `$${Math.round(value).toLocaleString()}`
        : Math.round(value).toLocaleString();
    }

    // 🔹 NORMAL LOGIC — All other values
    if (value >= 1_000_000) {
      const millions = value / 1_000_000;
      const formatted =
        millions >= 10 ? millions.toFixed(2) : millions.toFixed(1);
      return isCurrency ? `$${formatted}M` : `${formatted}M`;
    }

    if (value >= 1_0000) {
      const thousands = Math.floor(value / 1_000);
      return isCurrency ? `$${thousands}K` : `${thousands}K`;
    }

    return isCurrency
      ? `$${Math.round(value).toLocaleString()}`
      : value.toLocaleString();
  };

  const statCards = [
    {
      title: '# of Shipments',
      value: formatToMillions(totalShipments, false),
      tooltip: totalShipments.toLocaleString(), // 👈 exact value
      icon: <LocalShipping />,
      color: 'success',
      subtitle: 'All shipments'
    },
    {
      title: 'Total Freight Spent',
      value: formatToMillions(netFreightSpent, true, true),
      tooltip: `$${netFreightSpent.toLocaleString()}`,
      icon: <AttachMoney />,
      color: 'primary',
      subtitle: 'Total spent'
    },
    {
      title: '# of pounds moved',
      value: formatToMillions(totalPoundsMoved, false),
      tooltip: totalPoundsMoved.toLocaleString(),
      icon: <Scale />,
      color: 'warning',
      subtitle: 'Total weight'
    },
    {
      title: 'Cost per Pound',
      value: `$${costPerPound.toFixed(3)}`,
      tooltip: `$${costPerPound.toLocaleString(undefined, {
        minimumFractionDigits: 3
      })}`,
      icon: <Balance />,
      color: 'info',
      subtitle: 'Per pound cost'
    }
  ];

  // Add these optimized calculations before your return statement
  const costPerPoundData = React.useMemo(() => {
    const dataByMonth = {};

    // Process shipments
    shipments.forEach(shipment => {
      if (
        !shipment.actualPickupDate ||
        !shipment.totalGrossWeight ||
        !shipment.quoteNetCharge
      )
        return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = { totalWeight: 0, totalCost: 0, date };
      }

      dataByMonth[monthYear].totalWeight += shipment.totalGrossWeight || 0;
      dataByMonth[monthYear].totalCost += shipment.quoteNetCharge || 0;
    });

    // Get all unique month-years with their date objects
    const monthYearEntries = Object.entries(dataByMonth).map(
      ([monthYear, data]) => ({
        monthYear,
        ...data
      })
    );

    // Sort by date
    monthYearEntries.sort((a, b) => a.date - b.date);

    // Optional: Fill in missing months between earliest and latest dates
    if (monthYearEntries.length > 0) {
      const allMonths = [];
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const startDate = monthYearEntries[0].date;
      const endDate = monthYearEntries[monthYearEntries.length - 1].date;

      let currentDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        1
      );
      const endDateMonth = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        1
      );

      const existingMonths = new Set(
        monthYearEntries.map(item => item.monthYear)
      );

      while (currentDate <= endDateMonth) {
        const month = monthNames[currentDate.getMonth()];
        const year = currentDate.getFullYear().toString().slice(-2);
        const monthYear = `${month}-${year}`;

        if (!existingMonths.has(monthYear)) {
          // Add empty month entry
          monthYearEntries.push({
            monthYear,
            totalWeight: 0,
            totalCost: 0,
            date: new Date(currentDate)
          });
        }

        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      // Re-sort with the added months
      monthYearEntries.sort((a, b) => a.date - b.date);
    }

    return {
      categories: monthYearEntries.map(item => item.monthYear),
      data: monthYearEntries.map(item => {
        return item.totalWeight > 0 ? item.totalCost / item.totalWeight : 0;
      })
    };
  }, [shipments]);

  const avgCostPerShipmentData = React.useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.quoteNetCharge) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
      const monthYear = `${month}-${year}`; // Format: Oct-24, Nov-24

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = { totalCost: 0, count: 0 };
      }

      dataByMonth[monthYear].totalCost += shipment.quoteNetCharge || 0;
      dataByMonth[monthYear].count += 1;
    });

    // Create an array of month-year objects with proper dates for sorting
    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, data]) => {
        const [monthAbbr, year] = monthYear.split('-');
        // Create a proper date object for sorting (using 20XX for years)
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return {
          monthYear,
          totalCost: data.totalCost,
          count: data.count,
          date
        };
      })
      .sort((a, b) => a.date - b.date); // Sort by date

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => {
        return item.count > 0 ? item.totalCost / item.count : 0;
      })
    };
  }, [shipments]);

  // Calculate freight by weight ranges
  const calculateFreightByWeight = shipments => {
    const weightRanges = [
      { min: 0, max: 499, label: '<500' },
      { min: 500, max: 999, label: '500-999' },
      { min: 1000, max: 1999, label: '1,000-1,999' },
      { min: 2000, max: 4999, label: '2,000-4,999' },
      { min: 5000, max: 9999, label: '5,000-9,999' },
      { min: 10000, max: 19999, label: '10,000-19,999' },
      { min: 20000, max: 40000, label: '20,000-40,000' },
      { min: 40001, max: Infinity, label: '>40,000' }
    ];

    const freightByWeight = weightRanges.map(range => 0);

    shipments.forEach(shipment => {
      const weight = shipment.totalNetWeight || 0;
      const freight = shipment.quoteNetCharge || 0;

      for (let i = 0; i < weightRanges.length; i++) {
        if (weight >= weightRanges[i].min && weight <= weightRanges[i].max) {
          freightByWeight[i] += freight;
          break;
        }
      }
    });

    return freightByWeight;
  };

  // Fixed calculation function for freight by miles
  const calculateFreightByMiles = shipments => {
    const mileRanges = [
      { min: 0, max: 50, label: '0-49' },
      { min: 50, max: 100, label: '50-99' },
      { min: 100, max: 250, label: '100-249' },
      { min: 250, max: 500, label: '250-499' },
      { min: 500, max: 750, label: '500-749' },
      { min: 750, max: 1000, label: '750-999' },
      { min: 1000, max: 1500, label: '1,000-1,499' },
      { min: 1500, max: 2000, label: '1,500-1,999' },
      { min: 2000, max: 2500, label: '2,000-2,499' },
      { min: 2500, max: Infinity, label: '2,500+' }
    ];

    const freightByMiles = mileRanges.map(range => 0);

    shipments.forEach(shipment => {
      // Convert meters to miles and round to 2 decimal places
      const METERS_PER_MILE = 1609.344;
      const miles = shipment.shipmentMiles
        ? (shipment.shipmentMiles / METERS_PER_MILE).toFixed(0)
        : 0;
      const freight = shipment.quoteNetCharge || 0;

      for (let i = 0; i < mileRanges.length; i++) {
        const range = mileRanges[i];
        // For all ranges except the last one, use < instead of <= for the max
        const inRange =
          range.max === Infinity
            ? miles >= range.min
            : miles >= range.min && miles < range.max;

        if (inRange) {
          freightByMiles[i] += freight;
          break;
        }
      }
    });

    return freightByMiles;
  };

  // Usage
  const freightByWeightData = calculateFreightByWeight(shipments);
  const freightByMilesData = calculateFreightByMiles(shipments);

  // Enhanced calculation with sorting
  const calculateCarrierData = shipments => {
    const carrierMap = {};

    shipments.forEach(shipment => {
      const carrierName = shipment.carrierName;
      const shipmentCharge = shipment.quoteNetCharge || 0;

      if (carrierName) {
        if (!carrierMap[carrierName]) {
          carrierMap[carrierName] = {
            totalFreight: 0,
            shipmentCount: 0
          };
        }
        carrierMap[carrierName].totalFreight += shipmentCharge;
        carrierMap[carrierName].shipmentCount += 1;
      }
    });

    // Sort carriers by total freight (highest first)
    const sortedCarriers = Object.entries(carrierMap).sort(
      ([, a], [, b]) => b.totalFreight - a.totalFreight
    );

    const carrierNames = sortedCarriers.map(([name]) => name);
    const carrierSpend = sortedCarriers.map(([, data]) => data.totalFreight);

    return {
      carrierNames,
      carrierSpend,
      carrierMap // Keep this for tooltip usage
    };
  };

  // Usage
  const { carrierNames, carrierSpend, carrierMap } =
    calculateCarrierData(shipments);

  // Define these arrays for the click handlers
  const weightRangeNames = [
    '<500',
    '500-999',
    '1,000-1,999',
    '2,000-4,999',
    '5,000-9,999',
    '10,000-19,999',
    '20,000-40,000',
    '>40,000'
  ];

  const mileRangeNames = [
    '0-49',
    '50-99',
    '100-249',
    '250-499',
    '500-749',
    '750-999',
    '1,000-1,499',
    '1,500-1,999',
    '2,000-2,499',
    '2,500+'
  ];

  // Monthly shipment count data - FIXED
  const monthlyShipmentCountData = useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`; // Explicit format

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = 0;
      }
      dataByMonth[monthYear] += 1;
    });

    // Create an array of month-year objects with proper dates for sorting
    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, count]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, count, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => item.count)
    };
  }, [shipments]);

  // Monthly shipments by type - FIXED
  const monthlyShipmentTypeCountData = useMemo(() => {
    const dataByMonthType = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.shipmentType) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonthType[monthYear]) {
        dataByMonthType[monthYear] = { LTL: 0, TL: 0, Parcel: 0 };
      }

      const type = shipment.shipmentType;
      if (dataByMonthType[monthYear][type] !== undefined) {
        dataByMonthType[monthYear][type] += 1;
      }
    });

    // Create an array of month-year objects with proper dates for sorting
    const monthlyData = Object.entries(dataByMonthType)
      .map(([monthYear, types]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, ...types, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      series: [
        { name: 'LTL', data: monthlyData.map(item => item.LTL || 0) },
        { name: 'Parcel', data: monthlyData.map(item => item.Parcel || 0) },
        { name: 'TL', data: monthlyData.map(item => item.TL || 0) }
      ]
    };
  }, [shipments]);

  // Average Shipment Size (lbs) data - FIXED
  const avgShipmentSizeData = useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.totalGrossWeight) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = { totalWeight: 0, count: 0 };
      }
      dataByMonth[monthYear].totalWeight +=
        parseFloat(shipment.totalGrossWeight) || 0;
      dataByMonth[monthYear].count += 1;
    });

    // Create an array of month-year objects with proper dates for sorting
    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, data]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, ...data, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => {
        return item.count > 0 ? item.totalWeight / item.count : 0;
      })
    };
  }, [shipments]);

  // Pounds Moved per Month data - FIXED
  const poundsMovedMonthlyData = useMemo(() => {
    const dataByMonth = {};

    shipments.forEach(shipment => {
      if (!shipment.actualPickupDate || !shipment.totalNetWeight) return;

      const date = new Date(shipment.actualPickupDate);
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      const monthYear = `${month}-${year}`;

      if (!dataByMonth[monthYear]) {
        dataByMonth[monthYear] = 0;
      }
      dataByMonth[monthYear] += parseFloat(shipment.totalNetWeight) || 0;
    });

    // Create an array of month-year objects with proper dates for sorting
    const monthlyData = Object.entries(dataByMonth)
      .map(([monthYear, weight]) => {
        const [monthAbbr, year] = monthYear.split('-');
        const date = new Date(`01 ${monthAbbr} 20${year}`);
        return { monthYear, weight, date };
      })
      .sort((a, b) => a.date - b.date);

    return {
      categories: monthlyData.map(item => item.monthYear),
      data: monthlyData.map(item => item.weight)
    };
  }, [shipments]);

  // Process your shipments array to get top 5 carriers
  const normalizeCarrierName = name => {
    if (!name || typeof name !== 'string') return 'UNKNOWN';

    const cleaned = name.trim().toUpperCase();

    // Explicit carrier normalization (BI parity)
    if (cleaned.includes('XPO')) return 'XPO';

    if (
      cleaned.includes('R AND L') ||
      cleaned.includes('R & L') ||
      cleaned.includes('R & L CARRIER') ||
      cleaned.includes('RL CARRIER')
    ) {
      return 'RL Carriers';
    }

    // Generic cleanup (NO abbreviation)
    return cleaned
      .replace(/\s+/g, ' ')
      .replace(/,? INC\.?$/i, '')
      .replace(/,? LLC\.?$/i, '')
      .replace(/,? LTD\.?$/i, '');
  };

  const processTopCarriersData = shipments => {
    const carrierStats = shipments.reduce((acc, shipment) => {
      const carrierName = normalizeCarrierName(shipment.carrierName);

      if (!acc[carrierName]) {
        acc[carrierName] = {
          totalPounds: 0,
          shipmentCount: 0
        };
      }

      acc[carrierName].totalPounds += Number(shipment.totalGrossWeight) || 0;

      acc[carrierName].shipmentCount += 1;

      return acc;
    }, {});

    const sortedCarriers = Object.entries(carrierStats)
      .map(([carrierName, stats]) => ({
        carrierName,
        totalPounds: stats.totalPounds,
        shipmentCount: stats.shipmentCount
      }))
      .sort((a, b) => b.totalPounds - a.totalPounds)
      .slice(0, 5);

    return {
      carrierNames: sortedCarriers.map(c => c.carrierName),
      poundsData: sortedCarriers.map(c => c.totalPounds),
      shipmentsData: sortedCarriers.map(c => c.shipmentCount),
      details: sortedCarriers
    };
  };

  // Use it like this:
  const topCarriersData = processTopCarriersData(shipments);

  // First, create a function to process lane data
  // const processTopLanesData = shipments => {
  //   // Group by origin-destination lane
  //   const laneStats = shipments.reduce((acc, shipment) => {
  //     const originCity = shipment.originCity || 'Unknown';
  //     const destinationCity = shipment.destinationCity || 'Unknown';
  //     const lane = `${originCity}-${destinationCity}`;

  //     if (!acc[lane]) {
  //       acc[lane] = {
  //         shipmentCount: 0,
  //         originCity,
  //         destinationCity
  //       };
  //     }

  //     acc[lane].shipmentCount += 1;

  //     return acc;
  //   }, {});

  //   // Convert to array and sort by shipment count (descending)
  //   const sortedLanes = Object.entries(laneStats)
  //     .map(([laneName, stats]) => ({
  //       laneName,
  //       displayName: `${stats.originCity} → ${stats.destinationCity}`,
  //       shipmentCount: stats.shipmentCount,
  //       originCity: stats.originCity,
  //       destinationCity: stats.destinationCity
  //     }))
  //     .sort((a, b) => b.shipmentCount - a.shipmentCount)
  //     .slice(0, 5); // Take top 5

  //   return {
  //     laneNames: sortedLanes.map(l => l.laneName),
  //     displayNames: sortedLanes.map(l => l.displayName),
  //     shipmentCounts: sortedLanes.map(l => l.shipmentCount),
  //     details: sortedLanes
  //   };
  // };
  const processTopLanesData = shipments => {
    const normalize = val => val.toString().trim().toUpperCase();

    const isValidCity = val => typeof val === 'string' && val.trim().length > 0;

    const isDelivered = status =>
      typeof status === 'string' &&
      ['DELIVERED'].includes(status.toUpperCase());

    const laneStats = shipments.reduce((acc, shipment) => {
      if (
        !isDelivered(shipment.shipmentStatus) ||
        !isValidCity(shipment.originCity) ||
        !isValidCity(shipment.destinationCity)
      ) {
        return acc;
      }

      const originCity = normalize(shipment.originCity);
      const destinationCity = normalize(shipment.destinationCity);

      const laneKey = `${originCity}__${destinationCity}`;

      if (!acc[laneKey]) {
        acc[laneKey] = {
          shipmentCount: 0,
          originCity,
          destinationCity
        };
      }

      acc[laneKey].shipmentCount += 1;

      return acc;
    }, {});

    const sortedLanes = Object.entries(laneStats)
      .map(([laneKey, stats]) => ({
        laneName: laneKey,
        displayName: `${stats.originCity} → ${stats.destinationCity}`,
        shipmentCount: stats.shipmentCount,
        originCity: stats.originCity,
        destinationCity: stats.destinationCity
      }))
      .sort((a, b) => b.shipmentCount - a.shipmentCount)
      .slice(0, 5);

    return {
      laneNames: sortedLanes.map(l => l.laneName),
      displayNames: sortedLanes.map(l => l.displayName),
      shipmentCounts: sortedLanes.map(l => l.shipmentCount),
      details: sortedLanes
    };
  };

  // Use it
  const topLanesData = processTopLanesData(shipments);

  const processShipmentsByWeight = shipments => {
    // Initialize counters for each weight range
    const weightRanges = {
      under500: 0, // <500
      _500_999: 0, // 500-999
      _1000_1999: 0, // 1,000-1,999
      _2000_4999: 0, // 2,000-4,999
      _5000_9999: 0, // 5,000-9,999
      _10000_19999: 0, // 10,000-19,999 (added missing range)
      _20000_39000: 0, // 20,000-40,000
      over40000: 0 // >40,000
    };

    shipments.forEach(shipment => {
      const weight = shipment.totalNetWeight || 0;

      if (weight < 500) {
        weightRanges.under500++;
      } else if (weight >= 500 && weight <= 999) {
        weightRanges._500_999++;
      } else if (weight >= 1000 && weight <= 1999) {
        weightRanges._1000_1999++;
      } else if (weight >= 2000 && weight <= 4999) {
        weightRanges._2000_4999++;
      } else if (weight >= 5000 && weight <= 9999) {
        weightRanges._5000_9999++;
      } else if (weight >= 10000 && weight <= 19999) {
        weightRanges._10000_19999++;
      } else if (weight >= 20000 && weight <= 39000) {
        weightRanges._20000_39000++;
      } else if (weight >= 40000) {
        weightRanges.over40000++;
      }
    });

    // Return in the same order as y-axis categories
    return [
      weightRanges.under500,
      weightRanges._500_999,
      weightRanges._1000_1999,
      weightRanges._2000_4999,
      weightRanges._5000_9999,
      weightRanges._10000_19999,
      weightRanges._20000_39000,
      weightRanges.over40000
    ];
  };

  // Use it
  const shipmentsByWeightData = processShipmentsByWeight(shipments);

  // Donut chart click handler
  const handleDonutClick = (event, chartContext, config) => {
    const clickedIndex = config.seriesIndex;
    const labels = ['Outbound', 'Inbound', 'Transfer', 'Dropship'];
    const selectedType = labels[clickedIndex];

    if (selectedType) {
      handleFilterChange('shipmentTypeRule', selectedType);
    }
  };

  // Update shipTypePieOptions to include click handler
  const shipTypePieOptionsWithClick = {
    ...shipTypePieOptions,
    chart: {
      ...shipTypePieOptions.chart,
      events: {
        dataPointSelection: handleDonutClick
      }
    }
  };

  return (
    <>
      <MetaTags>
        <title>New Dashboard | TMS Shipping</title>
      </MetaTags>
      <DashboardContainer>
        {loading ? (
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '14vw'
            }}
          >
            <Grid sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant='body2' color='textSecondary'>
                Loading Dashboard...
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <>
            {/* Unified Expanded Chart Modal */}
            <Dialog
              fullScreen
              open={!!expandedChart}
              onClose={() => setExpandedChart(null)}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton
                    edge='start'
                    color='inherit'
                    onClick={() => setExpandedChart(null)}
                    aria-label='close'
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography
                    sx={{ ml: 2, flex: 1, color: '#ffffff !important' }}
                    variant='h6'
                    component='div'
                  >
                    {expandedChart === 'monthlyFreight' &&
                      'Monthly Freight Spend - Detailed View'}
                    {expandedChart === 'shipmentTypeMonthly' &&
                      'Monthly Freight by Shipment Type - Detailed View'}
                    {expandedChart === 'avgCostPerShipment' &&
                      'Average Cost per Shipment - Detailed View'}
                    {expandedChart === 'costPerPound' &&
                      'Cost per Pound - Detailed View'}
                    {expandedChart === 'carrierFreight' &&
                      'Freight Spent By Carrier - Detailed View'}
                    {expandedChart === 'freightByWeight' &&
                      'Freight Spend by Weight - Detailed View'}
                    {expandedChart === 'freightByMiles' &&
                      'Freight Spend by Miles - Detailed View'}
                    {expandedChart === 'shipmentTypePie' &&
                      'Freight Spend by Shipment Type - Detailed View'}
                    {expandedChart === 'originShipmentsMap' &&
                      'Origin Shipments Map - Detailed View'}
                    {expandedChart === 'destinationShipmentsMap' &&
                      'Destination Shipments Map - Detailed View'}
                    {expandedChart === 'monthlyShipments' &&
                      'Monthly Shipment Count - Detailed View'}
                    {expandedChart === 'shipmentTypeMonthlyCount' &&
                      'Monthly Shipments by Type - Detailed View'}
                    {expandedChart === 'avgShipmentSize' &&
                      'Average Shipment Size - Detailed View'}
                    {expandedChart === 'poundsMovedMonthly' &&
                      'Pounds Moved per Month - Detailed View'}
                    {expandedChart === 'shipmentsByTypeDonut' &&
                      '# of Shipments by Shipments Type - Detailed View'}
                    {expandedChart === 'topCarriers' &&
                      'Top 5 Carriers - Detailed View'}
                    {expandedChart === 'shipmentsByWeight' &&
                      'shipments By Weight - Detailed View'}
                  </Typography>
                </Toolbar>
              </AppBar>
              <Box sx={{ p: 3, height: 'calc(100vh - 64px)' }}>
                {/* Add these cases to your existing Dialog component */}

                {/* Top 5 Carriers Chart Expanded */}
                {expandedChart === 'topCarriers' && (
                  <Box sx={{ height: '100%' }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'line',
                          height: '100%',
                          toolbar: { show: true },
                          events: {
                            dataPointSelection: (
                              event,
                              chartContext,
                              config
                            ) => {
                              const selectedCarrier =
                                topCarriersData.carrierNames[
                                  config.dataPointIndex
                                ];
                              if (selectedCarrier) {
                                handleCarrierFilter(selectedCarrier);
                                setExpandedChart(null);
                              }
                            }
                          }
                        },
                        stroke: {
                          width: [0, 2]
                        },
                        xaxis: {
                          categories: topCarriersData.carrierNames,
                          labels: {
                            style: {
                              fontSize: '14px',
                              fontWeight: 'bold'
                            },
                            rotate: -45,
                            hideOverlappingLabels: false,
                            trim: false,
                            maxHeight: 80
                          },
                          title: {
                            text: 'Carriers',
                            style: {
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }
                          }
                        },
                        yaxis: [
                          {
                            seriesName: 'Pounds',
                            title: {
                              text: 'Total Pounds (K)',
                              style: {
                                fontSize: '16px',
                                fontWeight: 'bold'
                              }
                            },
                            labels: {
                              formatter: function (val) {
                                return (val / 1000).toFixed(0) + 'K';
                              },
                              style: {
                                fontSize: '14px'
                              }
                            }
                          },
                          {
                            seriesName: 'Shipments',
                            opposite: true,

                            labels: {
                              formatter: function (val) {
                                return Math.round(val).toLocaleString();
                              },
                              style: {
                                fontSize: '14px'
                              }
                            }
                          }
                        ],
                        colors: ['#008FFB', '#FF4560'],
                        fill: {
                          opacity: [0.85, 1]
                        },
                        grid: {
                          padding: {
                            bottom: 60
                          }
                        },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          y: {
                            formatter: function (
                              val,
                              { seriesIndex, dataPointIndex }
                            ) {
                              const carrierName =
                                topCarriersData.carrierNames[dataPointIndex];
                              if (seriesIndex === 0) {
                                return `${carrierName}<br/>Pounds: ${val.toLocaleString()} (${
                                  val / 1000
                                }K)`;
                              }
                              if (seriesIndex === 1) {
                                return `${carrierName}<br/>Shipments: ${val.toLocaleString()}`;
                              }
                              return val;
                            }
                          }
                        },
                        legend: {
                          position: 'top',
                          horizontalAlign: 'center',
                          fontSize: '14px',
                          itemMargin: {
                            horizontal: 20,
                            vertical: 10
                          }
                        }
                      }}
                      series={[
                        {
                          name: 'Total Pounds',
                          type: 'column',
                          data: topCarriersData.poundsData
                        },
                        {
                          name: 'Shipments',
                          type: 'line',
                          data: topCarriersData.shipmentsData
                        }
                      ]}
                      height={500}
                    />
                  </Box>
                )}

                {/* Top 5 Lanes Chart Expanded */}
                {expandedChart === 'topLanes' && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      height: '100%',
                      width: '100%'
                    }}
                  >
                    {/* Lane Names on Left */}
                    <Box
                      sx={{
                        width: '15%',
                        minWidth: 200,
                        pr: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: 500,
                        mt: 1.5 // Adjust to align with bars
                      }}
                    >
                      {topLanesData.displayNames.map((lane, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 90,
                            mb: 0
                          }}
                        >
                          <Typography
                            variant='body2'
                            sx={{
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#304758'
                            }}
                          >
                            {index + 1}. {lane}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Chart on Right */}
                    <Box
                      sx={{
                        width: '85%',
                        height: '100%'
                      }}
                    >
                      <ReactApexChart
                        options={{
                          chart: {
                            type: 'bar',
                            height: '100%',
                            toolbar: { show: true }
                          },
                          plotOptions: {
                            bar: {
                              horizontal: true,
                              barHeight: '40%',
                              borderRadius: 1,
                              dataLabels: {
                                position: 'right'
                              }
                            }
                          },
                          dataLabels: {
                            enabled: true,
                            formatter: function (val) {
                              return (
                                '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0' +
                                val.toLocaleString()
                              );
                            },
                            offsetX: 2,
                            style: {
                              fontSize: '14px',
                              colors: ['#304758'],
                              fontWeight: 'bold'
                            }
                          },
                          xaxis: {
                            labels: {
                              formatter: function (val) {
                                return val.toLocaleString();
                              },
                              style: {
                                fontSize: '14px'
                              }
                            }
                          },
                          yaxis: {
                            show: false, // Hide y-axis since we show names manually
                            labels: {
                              show: false
                            }
                          },
                          colors: ['#FEB019'],
                          tooltip: {
                            y: {
                              formatter: function (val, { dataPointIndex }) {
                                const lane =
                                  topLanesData.details[dataPointIndex];
                                return `${lane.originCity} → ${
                                  lane.destinationCity
                                }<br/>Shipments: ${val.toLocaleString()}`;
                              }
                            }
                          },
                          grid: {
                            padding: {
                              left: 0,
                              right: 4,
                              top: 0,
                              bottom: 2
                            },
                            yaxis: {
                              lines: {
                                show: false // Hide horizontal grid lines
                              }
                            }
                          },
                          title: {
                            text: 'Top 5 Shipping Lanes by Number of Shipments',
                            align: 'left',
                            style: {
                              fontSize: '18px',
                              fontWeight: 'bold'
                            }
                          }
                        }}
                        series={[
                          {
                            name: 'Shipments',
                            data: topLanesData.shipmentCounts
                          }
                        ]}
                        type='bar'
                        height={550}
                      />
                    </Box>
                  </Box>
                )}

                {/* Shipments by Weight Chart Expanded */}
                {expandedChart === 'shipmentsByWeight' && (
                  <Box sx={{ height: '100%' }}>
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: '100%',
                          toolbar: { show: true },
                          events: {
                            dataPointSelection: (
                              event,
                              chartContext,
                              config
                            ) => {
                              const selectedWeightRange =
                                weightRangeNames[config.dataPointIndex];
                              if (selectedWeightRange) {
                                handleWeightFilter(selectedWeightRange);
                                setExpandedChart(null);
                              }
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            distributed: true,
                            horizontal: true,
                            barHeight: '70%',
                            borderRadius: 4,
                            dataLabels: {
                              position: 'right',
                              hideOverflowingLabels: false
                            }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            const pad =
                              '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                            return pad + val.toLocaleString();
                          },
                          offsetX: 20,
                          style: {
                            fontSize: '14px',
                            colors: ['#304758'],
                            fontWeight: 'bold',
                            textShadow: '1px 1px 2px white'
                          },
                          background: {
                            enabled: false
                          }
                        },
                        xaxis: {
                          labels: {
                            formatter: function (val) {
                              return val.toLocaleString();
                            },
                            style: {
                              fontSize: '14px'
                            }
                          }
                        },
                        yaxis: {
                          labels: {
                            show: true,
                            minWidth: 100,
                            maxWidth: 120,
                            style: {
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }
                          }
                        },
                        legend: {
                          show: false
                        },
                        grid: {
                          padding: {
                            left: 20,
                            right: 40,
                            top: 20,
                            bottom: 20
                          }
                        },
                        colors: [
                          '#008FFB',
                          '#00E396',
                          '#FEB019',
                          '#FF4560',
                          '#775DD0',
                          '#3F51B5',
                          '#546E7A',
                          '#D4526E',
                          '#13d8aa'
                        ],
                        tooltip: {
                          y: {
                            formatter: function (val, { dataPointIndex, w }) {
                              const weightRanges = [
                                'Under 500 lbs',
                                '500-999 lbs',
                                '1,000-1,999 lbs',
                                '2,000-4,999 lbs',
                                '5,000-9,999 lbs',
                                '10,000-19,999 lbs',
                                '20,000-40,000 lbs',
                                'Over 40,000 lbs'
                              ];
                              const weightRange = weightRanges[dataPointIndex];
                              return `${weightRange}: ${val.toLocaleString()} shipments`;
                            }
                          }
                        },
                        // ADD THIS: Labels for the bars
                        labels: [
                          '<500 lbs',
                          '500-999 lbs',
                          '1,000-1,999 lbs',
                          '2,000-4,999 lbs',
                          '5,000-9,999 lbs',
                          '10,000-19,999 lbs',
                          '20,000-40,000 lbs',
                          '>40,000 lbs'
                        ]
                      }}
                      series={[
                        {
                          name: 'Shipments',
                          data: shipmentsByWeightData
                        }
                      ]}
                      type='bar'
                      height={500}
                    />
                  </Box>
                )}
                {expandedChart === 'shipmentsByTypeDonut' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'donut',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: handleDonutClick
                        }
                      },
                      plotOptions: {
                        pie: {
                          dataLabels: {
                            offset: 65,
                            minAngleToShowLabel: 0
                          },
                          donut: {
                            size: '65%',
                            labels: {
                              show: true,
                              name: {
                                show: true,
                                fontSize: '12px',
                                fontWeight: 'bold'
                              },
                              value: {
                                show: true,
                                fontSize: '16px',
                                fontWeight: 'bold',
                                formatter: function (val) {
                                  return val >= 1000
                                    ? (val / 1000).toFixed(1) + 'k'
                                    : val.toLocaleString();
                                }
                              }
                            }
                          }
                        }
                      },
                      dataLabels: {
                        style: {
                          fontSize: '10px',
                          fontWeight: 'bold',
                          colors: ['#1a237e']
                        },

                        enabled: true,
                        formatter: function (_val, opts) {
                          const actualValue =
                            opts.w.globals.series[opts.seriesIndex];
                          return actualValue.toLocaleString();
                        },

                        background: {
                          enabled: true,
                          foreColor: '#fff',
                          padding: 6,
                          borderRadius: 6,
                          opacity: 0.9
                        },

                        dropShadow: {
                          enabled: false
                        }
                      },
                      stroke: {
                        show: true,
                        width: 3
                      },
                      legend: {
                        position: 'top',
                        horizontalAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        markers: {
                          width: 12,
                          height: 12,
                          radius: 6
                        },
                        itemMargin: {
                          horizontal: 10,
                          vertical: 5
                        }
                      },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, opts) {
                            // Show actual count in tooltip
                            const labels = [
                              'Outbound',
                              'Inbound',
                              'Transfer',
                              'Dropship'
                            ];
                            const label =
                              labels[opts.seriesIndex] ||
                              `Type ${opts.seriesIndex + 1}`;
                            return `${label}: ${val.toLocaleString()} shipments`;
                          }
                        }
                      },
                      labels: ['Outbound', 'Inbound', 'Transfer', 'Dropship'],
                      responsive: [
                        {
                          breakpoint: 480,
                          options: {
                            chart: {
                              width: '100%'
                            },
                            legend: {
                              position: 'bottom'
                            }
                          }
                        }
                      ]
                    }}
                    series={
                      shipmentCountsByType
                        ? Object.values(shipmentCountsByType)
                        : [0, 0, 0, 0]
                    }
                    type='donut'
                    height='100%'
                  />
                )}
                {expandedChart === 'monthlyShipments' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyShipmentCountData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return val / 1000 + 'k';
                          }
                          return val;
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyShipmentCountData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        tickAmount: 6,
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return (val / 1000).toFixed(1) + 'k';
                            }
                            return val;
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 20,
                          right: 20
                        }
                      },
                      colors: ['#00E396'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return val.toLocaleString() + ' shipments';
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Shipment Count',
                        data: monthlyShipmentCountData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'shipmentTypeMonthlyCount' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyShipmentTypeCountData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return val / 1000 + 'k';
                          }
                          return val > 0 ? val : '';
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyShipmentTypeCountData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return (val / 1000).toFixed(1) + 'k';
                            }
                            return val > 0 ? val : '';
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 20,
                          right: 20
                        }
                      },
                      colors: ['#008FFB', '#00E396', '#FF4560'],
                      legend: {
                        position: 'top',
                        fontSize: '16px'
                      },
                      tooltip: {
                        y: {
                          formatter: function (val, { seriesIndex }) {
                            const type = ['LTL', 'Parcel', 'TL'][seriesIndex];
                            return (
                              type + ': ' + val.toLocaleString() + ' shipments'
                            );
                          }
                        }
                      }
                    }}
                    series={monthlyShipmentTypeCountData.series}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'avgShipmentSize' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              avgShipmentSizeData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return (val / 1000).toFixed(1) + 'k';
                          }
                          return Math.round(val);
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: avgShipmentSizeData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return (val / 1000).toFixed(1) + 'k';
                            }
                            return Math.round(val);
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 20,
                          right: 20
                        }
                      },
                      colors: ['#775DD0'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return Math.round(val).toLocaleString() + ' lbs';
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Avg Shipment Size',
                        data: avgShipmentSizeData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'poundsMovedMonthly' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              poundsMovedMonthlyData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return Math.round(val / 1000) + 'k';
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '14px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: poundsMovedMonthlyData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            return (val / 1000000).toFixed(1) + 'M';
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 20,
                          right: 20
                        }
                      },
                      colors: ['#FF4560'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return val.toLocaleString() + ' lbs';
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Pounds Moved',
                        data: poundsMovedMonthlyData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}
                {expandedChart === 'shipmentTypePie' && (
                  <ReactApexChart
                    options={{
                      ...shipTypePieOptions,
                      chart: {
                        ...shipTypePieOptions.chart,
                        height: '95%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: handleDonutClick
                        }
                      }
                    }}
                    series={shipTypePieSeries}
                    type='donut'
                    height='95%'
                  />
                )}
                {expandedChart === 'freightByWeight' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const weightRanges = [
                              '<500',
                              '500-999',
                              '1,000-1,999',
                              '2,000-4,999',
                              '5,000-9,999',
                              '10,000-19,999',
                              '20,000-40,000',
                              '>40,000'
                            ];
                            const selectedWeightRange =
                              weightRanges[config.dataPointIndex];
                            if (selectedWeightRange) {
                              handleWeightFilter(selectedWeightRange);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '80%', // Slightly larger for expanded view
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          if (val >= 1000) {
                            return pad + '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: {
                          enabled: false
                        },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: {
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }
                        }
                      },
                      yaxis: {
                        categories: [
                          '<500',
                          '500-999',
                          '1,000-1,999',
                          '2,000-4,999',
                          '5,000-9,999',
                          '10,000-19,999',
                          '20,000-40,000',
                          '>40,000'
                        ],
                        labels: {
                          style: {
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }
                        }
                      },
                      grid: {
                        padding: { left: 20, right: 20 }
                      },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, { dataPointIndex, w }) {
                            const weightRanges = [
                              '<500 lbs',
                              '500-999 lbs',
                              '1,000-1,999 lbs',
                              '2,000-4,999 lbs',
                              '5,000-9,999 lbs',
                              '10,000-19,999 lbs',
                              '20,000-40,000 lbs',
                              '>40,000 lbs'
                            ];
                            const weightRange = weightRanges[dataPointIndex];
                            return `${weightRange}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '<500',
                        '500-999',
                        '1,000-1,999',
                        '2,000-4,999',
                        '5,000-9,999',
                        '10,000-19,999',
                        '20,000-40,000',
                        '>40,000'
                      ]
                    }}
                    series={[
                      {
                        name: 'Freight Spend',
                        data: freightByWeightData
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'freightByMiles' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const mileRanges = [
                              '0-49',
                              '50-99',
                              '100-249',
                              '250-499',
                              '500-749',
                              '750-999',
                              '1,000-1,499',
                              '1,500-1,999',
                              '2,000-2,499',
                              '2,500+'
                            ];
                            const selectedMileRange =
                              mileRanges[config.dataPointIndex];
                            if (selectedMileRange) {
                              handleMileFilter(selectedMileRange);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '80%', // Slightly larger for expanded view
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          if (val >= 1000) {
                            return pad + '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return pad + '$' + Math.round(val);
                        },
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: {
                          enabled: false
                        },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: { fontSize: '14px' }
                        },
                        title: {
                          text: 'Freight Spend',
                          style: {
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }
                        }
                      },
                      yaxis: {
                        categories: [
                          '0-49',
                          '50-99',
                          '100-249',
                          '250-499',
                          '500-749',
                          '750-999',
                          '1,000-1,499',
                          '1,500-1,999',
                          '2,000-2,499',
                          '2,500+'
                        ],
                        labels: {
                          style: {
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }
                        }
                      },
                      grid: {
                        padding: { left: 20, right: 20 }
                      },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, { dataPointIndex, w }) {
                            const mileRanges = [
                              '0-49 miles',
                              '50-99 miles',
                              '100-249 miles',
                              '250-499 miles',
                              '500-749 miles',
                              '750-999 miles',
                              '1,000-1,499 miles',
                              '1,500-1,999 miles',
                              '2,000-2,499 miles',
                              '2,500+ miles'
                            ];
                            const mileRange = mileRanges[dataPointIndex];
                            return `${mileRange}: $${val.toLocaleString()}`;
                          }
                        }
                      },
                      labels: [
                        '0-49',
                        '50-99',
                        '100-249',
                        '250-499',
                        '500-749',
                        '750-999',
                        '1,000-1,499',
                        '1,500-1,999',
                        '2,000-2,499',
                        '2,500+'
                      ]
                    }}
                    series={[
                      {
                        name: 'Freight Spend',
                        data: freightByMilesData
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}
                {expandedChart === 'carrierFreight' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedCarrier =
                              carrierNames[config.dataPointIndex];
                            if (selectedCarrier) {
                              handleCarrierFilter(selectedCarrier);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '95%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          const millions = val / 1000000;
                          return (
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0$' +
                            millions.toFixed(1) +
                            'M'
                          );
                        },
                        style: {
                          fontSize: '14px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: {
                          enabled: false
                        },
                        offsetX: 20
                      },
                      xaxis: {
                        min: Math.max(...carrierSpend) * 0.0,
                        max: Math.max(...carrierSpend) * 1.1,
                        labels: {
                          formatter: function (val) {
                            const millions = val / 1000000;
                            return '$' + millions.toFixed(1) + 'M';
                          },
                          style: { fontSize: '14px' }
                        },
                        title: {
                          text: 'Freight Spend (Millions)',
                          style: {
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }
                        }
                      },
                      yaxis: {
                        categories: carrierNames,
                        labels: {
                          style: {
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }
                        }
                      },
                      grid: {
                        padding: { left: 20, right: 20 }
                      },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E',
                        '#8D5B4C',
                        '#F86624'
                      ],
                      tooltip: {
                        y: {
                          formatter: function (val, { dataPointIndex, w }) {
                            const carrierName = w.config.labels[dataPointIndex];
                            const shipmentCount =
                              carrierMap[carrierName]?.shipmentCount || 0;
                            return `${carrierName}: $${val.toLocaleString()} (${shipmentCount} shipments)`;
                          }
                        }
                      },
                      labels: carrierNames
                    }}
                    series={[
                      {
                        name: 'Freight Spend',
                        data: carrierSpend
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}
                {expandedChart === 'monthlyFreight' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyData[config.dataPointIndex]?.month;
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return '$' + Math.round(val);
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyData.map(item => item.month),
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 15,
                          right: 15
                        }
                      },
                      colors: ['#008FFB'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return 'Total Freight: $' + val.toLocaleString();
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Freight Spend',
                        data: monthlyData.map(item => Math.round(item.freight))
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'shipmentTypeMonthly' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              monthlyShipmentTypeData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          if (val >= 1000) {
                            return '$' + (val / 1000).toFixed(0) + 'K';
                          }
                          return val > 0 ? '$' + Math.round(val) : '';
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: monthlyShipmentTypeData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            if (val >= 1000) {
                              return '$' + (val / 1000).toFixed(0) + 'K';
                            }
                            return '$' + Math.round(val);
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 15,
                          right: 15
                        }
                      },
                      colors: ['#008FFB', '#00E396', '#FF4560'],
                      legend: {
                        position: 'top',
                        fontSize: '14px'
                      },
                      tooltip: {
                        y: {
                          formatter: function (val, { seriesIndex }) {
                            const type = ['LTL', 'TL', 'Parcel'][seriesIndex];
                            return type + ': $' + val.toLocaleString();
                          }
                        }
                      }
                    }}
                    series={monthlyShipmentTypeData.series}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'avgCostPerShipment' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              avgCostPerShipmentData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return '$' + Math.round(val).toLocaleString();
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: avgCostPerShipmentData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            return '$' + Math.round(val).toLocaleString();
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 15,
                          right: 15
                        }
                      },
                      colors: ['#775DD0'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return (
                              'Avg Cost: $' + Math.round(val).toLocaleString()
                            );
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Avg Cost per Shipment',
                        data: avgCostPerShipmentData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}

                {expandedChart === 'costPerPound' && (
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: '100%',
                        toolbar: { show: true },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedMonth =
                              costPerPoundData.categories[
                                config.dataPointIndex
                              ];
                            if (selectedMonth) {
                              handleMonthFilter(selectedMonth);
                              setExpandedChart(null);
                            }
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          borderRadius: 2,
                          columnWidth: '50%',
                          dataLabels: {
                            position: 'top'
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: function (val) {
                          return '$' + val.toFixed(3);
                        },
                        offsetY: -20,
                        style: {
                          fontSize: '12px',
                          colors: ['#304758'],
                          fontWeight: 'bold'
                        }
                      },
                      xaxis: {
                        categories: costPerPoundData.categories,
                        labels: {
                          style: {
                            fontSize: '14px'
                          },
                          rotate: -45,
                          trim: false
                        }
                      },
                      yaxis: {
                        labels: {
                          formatter: function (val) {
                            return '$' + val.toFixed(1);
                          },
                          style: {
                            fontSize: '14px'
                          }
                        }
                      },
                      grid: {
                        padding: {
                          left: 15,
                          right: 15
                        }
                      },
                      colors: ['#FF4560'],
                      tooltip: {
                        y: {
                          formatter: function (val) {
                            return 'Cost per Pound: $' + val.toFixed(2);
                          }
                        }
                      }
                    }}
                    series={[
                      {
                        name: 'Cost per Pound',
                        data: costPerPoundData.data
                      }
                    ]}
                    type='bar'
                    height='100%'
                  />
                )}
              </Box>
            </Dialog>

            {/* Calculate available tabs and their indexes */}
            {(() => {
              // Build array of available tab keys and their display indexes
              const availableTabs = [];

              // Helper to add tab if permission exists
              const addTabIfAllowed = (level, key, icon, label) => {
                if (
                  userPermissionsArr.filter(
                    permissions => permissions[`level${level}`]
                  ).length ||
                  UserIsAdmin
                ) {
                  availableTabs.push({ key, icon, label, level });
                }
              };

              // Check each permission level
              addTabIfAllowed(
                1,
                'freightSpend',
                <AttachMoney />,
                'Freight Spend'
              );
              addTabIfAllowed(
                2,
                'shipments',
                <LocalShipping />,
                '# of Shipments'
              );
              addTabIfAllowed(3, 'origin', <Place />, 'Origin');
              addTabIfAllowed(4, 'destination', <Map />, 'Destination');
              addTabIfAllowed(
                5,
                'onTimeDelivery',
                <Schedule />,
                'On-Time Delivery'
              );
              addTabIfAllowed(
                8,
                'weeklyTrend',
                <CalendarToday />,
                'Weekly Trend'
              );
              addTabIfAllowed(9, 'claims', <GavelIcon />, 'Claims');

              // Get current tab key based on activeTab index
              const currentTab = availableTabs[activeTab] || availableTabs[0];

              return (
                <>
                  {/* Tabs Section */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 0.1,
                      position: 'absolute',
                      top: 20,
                      zIndex: 1,
                      width: '86.5%'
                    }}
                  >
                    <CompactTabs
                      value={activeTab}
                      onChange={(e, newValue) => setActiveTab(newValue)}
                      variant='scrollable'
                      scrollButtons='auto'
                    >
                      {availableTabs.map((tab, index) => (
                        <Tab
                          key={tab.key}
                          icon={tab.icon}
                          label={tab.label}
                          iconPosition='start'
                        />
                      ))}
                    </CompactTabs>
                  </Paper>

                  {/* Top Filters Section - Single Line (show for all tabs except Claims) */}
                  {currentTab && currentTab.key !== 'claims' && (
                    <Paper elevation={1} sx={{ p: 1, mb: 0 }}>
                      <Grid container spacing={0.3} alignItems='center'>
                        {/* Customer Filter */}
                        <Grid item xs={4} sm={3} md={2} lg={1.8}>
                          <SearchableSelect
                            label='Customer'
                            value={filters.customer}
                            options={customers.filter(c => c !== 'All')}
                            onChange={e =>
                              handleFilterChange('customer', e.target.value)
                            }
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Origin Filter */}
                        <Grid item xs={4} sm={2.5} md={1.8} lg={1}>
                          <SearchableSelect
                            label='Origin'
                            value={filters.originState}
                            options={originStates.filter(s => s !== 'All')}
                            onChange={e =>
                              handleFilterChange('originState', e.target.value)
                            }
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Destination Filter */}
                        <Grid item xs={4} sm={2.5} md={1.8} lg={1}>
                          <SearchableSelect
                            label='Destination'
                            value={filters.destinationState}
                            options={destinationStates.filter(s => s !== 'All')}
                            onChange={e =>
                              handleFilterChange(
                                'destinationState',
                                e.target.value
                              )
                            }
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Mode Filter */}
                        <Grid item xs={4} sm={2.5} md={1.8} lg={1.2}>
                          <SearchableSelect
                            label='Shipment Mode'
                            value={filters.shipmentType}
                            options={shipmentTypes.filter(t => t !== 'All')}
                            onChange={e =>
                              handleFilterChange('shipmentType', e.target.value)
                            }
                            sx={{
                              fontSize: '0.75rem',
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Type Filter */}
                        <Grid item xs={4} sm={2.5} md={1.8} lg={1.4}>
                          <SearchableSelect
                            label='Shipment Type'
                            value={filters.shipmentTypeRule}
                            options={shipmentModes.filter(m => m !== 'All')}
                            onChange={e =>
                              handleFilterChange(
                                'shipmentTypeRule',
                                e.target.value
                              )
                            }
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Carriers Filter */}
                        <Grid item xs={4} sm={3} md={2} lg={1.3}>
                          <SearchableSelect
                            label='Carriers'
                            value={filters.carrier}
                            options={carriers.filter(c => c !== 'All')}
                            onChange={e =>
                              handleFilterChange('carrier', e.target.value)
                            }
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Date Range Filter */}
                        <Grid item xs={4} sm={3} md={2} lg={1.3}>
                          <FormControl fullWidth size='small'>
                            <InputLabel sx={{ fontSize: '0.75rem' }}>
                              Date Range
                            </InputLabel>
                            <Select
                              value={filters.dateRange}
                              label='Date Range'
                              onChange={e =>
                                handleFilterChange('dateRange', e.target.value)
                              }
                              sx={{ fontSize: '0.75rem' }}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 300
                                  }
                                }
                              }}
                            >
                              <MenuItem
                                value='All'
                                sx={{ fontSize: '0.75rem' }}
                              >
                                All Dates
                              </MenuItem>
                              <MenuItem
                                value='last7'
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Last 7 Days
                              </MenuItem>
                              <MenuItem
                                value='last30'
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Last 30 Days
                              </MenuItem>
                              <MenuItem
                                value='last90'
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Last 90 Days
                              </MenuItem>
                              <MenuItem
                                value='custom'
                                sx={{ fontSize: '0.75rem' }}
                              >
                                Custom
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Custom Date Range */}
                        {filters.dateRange === 'custom' && (
                          <>
                            <Grid item xs={4} sm={2.5} md={1.8} lg={1.4}>
                              <TextField
                                label='Start'
                                type='date'
                                size='small'
                                value={filters.startDate || ''}
                                onChange={e =>
                                  handleFilterChange(
                                    'startDate',
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{
                                  shrink: true,
                                  sx: { fontSize: '0.75rem' }
                                }}
                                inputProps={{
                                  sx: { fontSize: '0.75rem', py: 0.5 }
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={4} sm={2.5} md={1.8} lg={1.4}>
                              <TextField
                                label='End'
                                type='date'
                                size='small'
                                value={filters.endDate || ''}
                                onChange={e =>
                                  handleFilterChange('endDate', e.target.value)
                                }
                                InputLabelProps={{
                                  shrink: true,
                                  sx: { fontSize: '0.75rem' }
                                }}
                                inputProps={{
                                  sx: { fontSize: '0.75rem', py: 0.5 }
                                }}
                                fullWidth
                              />
                            </Grid>
                          </>
                        )}

                        {/* Week Filter */}
                        <Grid item xs={4} sm={2.5} md={1.8} lg={1.1}>
                          <SearchableSelect
                            label='Week'
                            value={filters.week}
                            options={weeks.filter(w => w !== 'All')}
                            onChange={e =>
                              handleFilterChange('week', e.target.value)
                            }
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': { fontSize: '0.75rem' }
                            }}
                            size='small'
                          />
                        </Grid>

                        {/* Reset Button */}
                        <Grid item xs={4} sm={2} md={1.5} lg={1}>
                          <Button
                            variant='contained'
                            startIcon={
                              <Refresh sx={{ fontSize: '0.875rem' }} />
                            }
                            onClick={resetFilters}
                            size='small'
                            sx={{
                              width: '100%',
                              minWidth: 'auto',
                              fontSize: '0.7rem',
                              py: 0.5,
                              px: 1
                            }}
                          >
                            Reset
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Render Tab Content based on current tab key */}
                  <Box sx={{ mt: 6 }}>
                    {currentTab && (
                      <>
                        {/* Freight Spend Tab */}
                        {currentTab.key === 'freightSpend' && (
                          <Box>
                            {/* Stats Cards */}
                            <Grid container spacing={0.5} sx={{ mb: 1 }}>
                              <Grid item xs={12} md={9}>
                                <Grid container spacing={0.5}>
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Grid
                                        container
                                        spacing={0.5}
                                        sx={{
                                          mb: 1
                                        }}
                                      >
                                        {statCards.map((card, index) => (
                                          <Grid item xs={12} sm={6} key={index}>
                                            <StatCard color={card.color}>
                                              <CardContent
                                                sx={{
                                                  p: 1,
                                                  '&:last-child': { pb: 1 },
                                                  height: '20px'
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                      'space-between',
                                                    alignItems: 'center'
                                                  }}
                                                >
                                                  <Box>
                                                    <Tooltip
                                                      title={card.tooltip}
                                                      arrow
                                                      placement='top'
                                                    >
                                                      <Typography
                                                        variant='h4'
                                                        component='div'
                                                        fontWeight='bold'
                                                        sx={{
                                                          cursor: 'pointer'
                                                        }}
                                                      >
                                                        {card.value}
                                                      </Typography>
                                                    </Tooltip>
                                                    <Typography
                                                      variant='body2'
                                                      sx={{
                                                        opacity: 0.9,
                                                        fontSize: '0.75rem'
                                                      }}
                                                    >
                                                      {card.title}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </CardContent>
                                            </StatCard>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </ChartContainer>
                                  </Grid>

                                  {/* Row 1 */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Monthly Freight Spend
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('monthlyFreight')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  monthlyData[
                                                    config.dataPointIndex
                                                  ]?.month;
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              if (val >= 1000) {
                                                return (
                                                  '$' +
                                                  (val / 1000).toFixed(0) +
                                                  'K'
                                                );
                                              }
                                              return '$' + Math.round(val);
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            categories: monthlyData.map(
                                              item => item.month
                                            ),
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            tickAmount: 6,
                                            labels: {
                                              formatter: function (val) {
                                                if (val >= 1000) {
                                                  return (
                                                    '$' +
                                                    (val / 1000).toFixed(0) +
                                                    'K'
                                                  );
                                                }
                                                return '$' + Math.round(val);
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: ['#008FFB'],
                                          tooltip: {
                                            y: {
                                              formatter: function (val) {
                                                return (
                                                  'Total Freight: $' +
                                                  val.toLocaleString()
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={[
                                          {
                                            name: 'Freight Spend',
                                            data: monthlyData.map(item =>
                                              Math.round(item.freight)
                                            )
                                          }
                                        ]}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>

                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Monthly Freight by Shipment Type
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart(
                                              'shipmentTypeMonthly'
                                            )
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  monthlyShipmentTypeData
                                                    .categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              if (val >= 1000) {
                                                return (
                                                  '$' +
                                                  (val / 1000).toFixed(0) +
                                                  'K'
                                                );
                                              }
                                              return val > 0
                                                ? '$' + Math.round(val)
                                                : '';
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              monthlyShipmentTypeData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            labels: {
                                              formatter: function (val) {
                                                if (val >= 1000) {
                                                  return (
                                                    '$' +
                                                    (val / 1000).toFixed(0) +
                                                    'K'
                                                  );
                                                }
                                                return '$' + Math.round(val);
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: [
                                            '#008FFB',
                                            '#00E396',
                                            '#FF4560'
                                          ],
                                          legend: {
                                            position: 'top',
                                            fontSize: '12px'
                                          },
                                          tooltip: {
                                            y: {
                                              formatter: function (
                                                val,
                                                { seriesIndex }
                                              ) {
                                                const type = [
                                                  'LTL',
                                                  'Parcel',
                                                  'TL'
                                                ][seriesIndex];
                                                return (
                                                  type +
                                                  ': $' +
                                                  val.toLocaleString()
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={monthlyShipmentTypeData.series}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Freight Spent By Carrier
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('carrierFreight')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>

                                      <Box
                                        sx={{
                                          overflowX: 'auto',
                                          overflowY: 'visible'
                                        }}
                                      >
                                        <ReactApexChart
                                          options={{
                                            chart: {
                                              type: 'bar',
                                              height: 280,
                                              toolbar: { show: false },
                                              events: {
                                                dataPointSelection: (
                                                  event,
                                                  chartContext,
                                                  config
                                                ) => {
                                                  const selectedCarrier =
                                                    carrierNames[
                                                      config.dataPointIndex
                                                    ];
                                                  if (selectedCarrier) {
                                                    handleCarrierFilter(
                                                      selectedCarrier
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                            legend: { show: false },
                                            plotOptions: {
                                              bar: {
                                                distributed: true,
                                                horizontal: true,
                                                barHeight: '95%',
                                                dataLabels: {
                                                  position: 'right',
                                                  hideOverflowingLabels: false
                                                }
                                              }
                                            },
                                            dataLabels: {
                                              enabled: true,
                                              formatter: function (val) {
                                                const millions = val / 1000000;
                                                return (
                                                  '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0$' +
                                                  millions.toFixed(1) +
                                                  'M'
                                                );
                                              },
                                              style: {
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                colors: ['#304758'],
                                                textShadow: '1px 1px 2px white'
                                              },
                                              background: {
                                                enabled: false
                                              },
                                              offsetX: 10
                                            },
                                            xaxis: {
                                              min:
                                                Math.max(...carrierSpend) * 0.0,
                                              max:
                                                Math.max(...carrierSpend) * 1.1,
                                              labels: {
                                                formatter: function (val) {
                                                  const millions =
                                                    val / 1000000;
                                                  return (
                                                    '$' +
                                                    millions.toFixed(1) +
                                                    'M'
                                                  );
                                                },
                                                style: { fontSize: '11px' }
                                              }
                                            },
                                            yaxis: {
                                              lines: {
                                                show: false // Hide horizontal grid lines
                                              },
                                              categories: carrierNames,
                                              labels: {
                                                show: true,
                                                minWidth: 60,
                                                maxWidth: 60,
                                                style: {
                                                  fontSize: '11px'
                                                  // fontWeight: 'bold'
                                                }
                                              }
                                            },
                                            grid: {
                                              padding: { left: 10, right: 15 }
                                            },
                                            colors: [
                                              '#008FFB',
                                              '#00E396',
                                              '#FEB019',
                                              '#FF4560',
                                              '#775DD0',
                                              '#3F51B5',
                                              '#546E7A',
                                              '#D4526E',
                                              '#8D5B4C',
                                              '#F86624',
                                              '#663399',
                                              '#E91E63',
                                              '#9C27B0',
                                              '#673AB7',
                                              '#3F51B5'
                                            ],
                                            tooltip: {
                                              y: {
                                                formatter: function (
                                                  val,
                                                  { dataPointIndex, w }
                                                ) {
                                                  const carrierName =
                                                    w.config.labels[
                                                      dataPointIndex
                                                    ];
                                                  const shipmentCount =
                                                    carrierMap[carrierName]
                                                      ?.shipmentCount || 0;
                                                  return `${carrierName}: $${val.toLocaleString()} (${shipmentCount} shipments)`;
                                                }
                                              }
                                            },
                                            labels: carrierNames
                                          }}
                                          series={[
                                            {
                                              name: 'Freight Spend',
                                              data: carrierSpend
                                            }
                                          ]}
                                          type='bar'
                                          height={280}
                                        />
                                      </Box>
                                    </ChartContainer>
                                  </Grid>

                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Average Cost per Shipment
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart(
                                              'avgCostPerShipment'
                                            )
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  avgCostPerShipmentData
                                                    .categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              return '$' + val.toFixed(0);
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              avgCostPerShipmentData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            labels: {
                                              formatter: function (val) {
                                                return (
                                                  '$' +
                                                  (val / 1000).toFixed(1) +
                                                  'K'
                                                );
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: ['#775DD0'],
                                          tooltip: {
                                            y: {
                                              formatter: function (val) {
                                                return (
                                                  'Avg Cost: $' +
                                                  Math.round(
                                                    val
                                                  ).toLocaleString()
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={[
                                          {
                                            name: 'Avg Cost per Shipment',
                                            data: avgCostPerShipmentData.data
                                          }
                                        ]}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>

                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Cost per Pound
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('costPerPound')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  costPerPoundData.categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              return '$' + val.toFixed(3);
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              costPerPoundData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            labels: {
                                              formatter: function (val) {
                                                return '$' + val.toFixed(1);
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: ['#FF4560'],
                                          tooltip: {
                                            y: {
                                              formatter: function (val) {
                                                return (
                                                  'Cost per Pound: $' +
                                                  val.toFixed(2)
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={[
                                          {
                                            name: 'Cost per Pound',
                                            data: costPerPoundData.data
                                          }
                                        ]}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>

                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Freight Spend by Weight (lbs)
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('freightByWeight')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>

                                      <Box
                                        sx={{
                                          overflowX: 'auto',
                                          overflowY: 'visible'
                                        }}
                                      >
                                        <ReactApexChart
                                          options={{
                                            chart: {
                                              type: 'bar',
                                              height: 250,
                                              toolbar: { show: false },
                                              legend: { show: false },
                                              events: {
                                                dataPointSelection: (
                                                  event,
                                                  chartContext,
                                                  config
                                                ) => {
                                                  const weightRanges = [
                                                    '<500',
                                                    '500-999',
                                                    '1,000-1,999',
                                                    '2,000-4,999',
                                                    '5,000-9,999',
                                                    '10,000-19,999',
                                                    '20,000-40,000',
                                                    '>40,000'
                                                  ];
                                                  const selectedWeightRange =
                                                    weightRanges[
                                                      config.dataPointIndex
                                                    ];
                                                  if (selectedWeightRange) {
                                                    handleWeightFilter(
                                                      selectedWeightRange
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                            plotOptions: {
                                              bar: {
                                                distributed: true,
                                                horizontal: true,
                                                barHeight: '60%', // Reduced from 95% to 60% for compact bars
                                                dataLabels: {
                                                  position: 'right',
                                                  hideOverflowingLabels: false
                                                }
                                              }
                                            },
                                            legend: { show: false },
                                            dataLabels: {
                                              enabled: true,
                                              formatter: function (val) {
                                                const pad =
                                                  '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';

                                                if (val >= 1000) {
                                                  return (
                                                    pad +
                                                    '$' +
                                                    (val / 1000).toFixed(0) +
                                                    'K'
                                                  );
                                                }
                                                return (
                                                  pad + '$' + Math.round(val)
                                                );
                                              },
                                              style: {
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                colors: ['#304758'],
                                                textShadow: '1px 1px 2px white'
                                              },
                                              background: {
                                                enabled: false
                                              },
                                              offsetX: 2
                                            },
                                            xaxis: {
                                              labels: {
                                                formatter: function (val) {
                                                  if (val >= 1000) {
                                                    return (
                                                      '$' +
                                                      (val / 1000).toFixed(0) +
                                                      'K'
                                                    );
                                                  }
                                                  return '$' + Math.round(val);
                                                },
                                                style: { fontSize: '11px' }
                                              },
                                              title: {
                                                text: 'Freight Spend',
                                                style: {
                                                  fontSize: '12px',
                                                  fontWeight: 'bold'
                                                }
                                              }
                                            },
                                            yaxis: {
                                              lines: {
                                                show: false // Hide horizontal grid lines
                                              },
                                              categories: [
                                                '<500',
                                                '500-999',
                                                '1,000-1,999',
                                                '2,000-4,999',
                                                '5,000-9,999',
                                                '10,000-19,999',
                                                '20,000-40,000',
                                                '>40,000'
                                              ],
                                              labels: {
                                                show: true,
                                                minWidth: 100,
                                                maxWidth: 120,
                                                style: {
                                                  fontSize: '11px',
                                                  fontWeight: 'bold'
                                                }
                                              }
                                            },
                                            grid: {
                                              padding: { left: -5, right: 15 }
                                            },
                                            colors: [
                                              '#008FFB',
                                              '#00E396',
                                              '#FEB019',
                                              '#FF4560',
                                              '#775DD0',
                                              '#3F51B5',
                                              '#546E7A'
                                            ],
                                            tooltip: {
                                              y: {
                                                formatter: function (
                                                  val,
                                                  { dataPointIndex, w }
                                                ) {
                                                  const weightRanges = [
                                                    '<500',
                                                    '500-999',
                                                    '1,000-1,999',
                                                    '2,000-4,999',
                                                    '5,000-9,999',
                                                    '10,000-19,999',
                                                    '20,000-40,000',
                                                    '>40,000'
                                                  ];
                                                  const weightRange =
                                                    weightRanges[
                                                      dataPointIndex
                                                    ];
                                                  return `${weightRange}: $${val.toLocaleString()}`;
                                                }
                                              }
                                            },
                                            labels: [
                                              '<500',
                                              '500-999',
                                              '1,000-1,999',
                                              '2,000-4,999',
                                              '5,000-9,999',
                                              '10,000-19,999',
                                              '20,000-40,000',
                                              '>40,000'
                                            ]
                                          }}
                                          series={[
                                            {
                                              name: 'Freight Spend',
                                              data: freightByWeightData
                                            }
                                          ]}
                                          type='bar'
                                          height={250}
                                        />
                                      </Box>
                                    </ChartContainer>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Freight Spend by Miles
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('freightByMiles')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>

                                      <Box
                                        sx={{
                                          overflowX: 'auto',
                                          overflowY: 'visible'
                                        }}
                                      >
                                        <ReactApexChart
                                          options={{
                                            chart: {
                                              type: 'bar',
                                              height: 280,
                                              toolbar: { show: false },
                                              events: {
                                                dataPointSelection: (
                                                  event,
                                                  chartContext,
                                                  config
                                                ) => {
                                                  const selectedMileRange =
                                                    mileRangeNames[
                                                      config.dataPointIndex
                                                    ];
                                                  if (selectedMileRange) {
                                                    handleMileFilter(
                                                      selectedMileRange
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                            plotOptions: {
                                              bar: {
                                                distributed: true,
                                                horizontal: true,
                                                barHeight: '60%',
                                                dataLabels: {
                                                  position: 'right',
                                                  hideOverflowingLabels: false
                                                }
                                              }
                                            },
                                            legend: { show: false },
                                            dataLabels: {
                                              enabled: true,
                                              formatter: function (val) {
                                                const pad =
                                                  '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                                                if (val >= 1000) {
                                                  return (
                                                    pad +
                                                    '$' +
                                                    (val / 1000).toFixed(0) +
                                                    'K'
                                                  );
                                                }
                                                return (
                                                  pad + '$' + Math.round(val)
                                                );
                                              },
                                              style: {
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                colors: ['#304758'],
                                                textShadow: '1px 1px 2px white'
                                              },
                                              background: {
                                                enabled: false
                                              },
                                              offsetX: 20
                                            },
                                            xaxis: {
                                              labels: {
                                                formatter: function (val) {
                                                  if (val >= 1000) {
                                                    return (
                                                      '$' +
                                                      (val / 1000).toFixed(0) +
                                                      'K'
                                                    );
                                                  }
                                                  return '$' + Math.round(val);
                                                },
                                                style: { fontSize: '11px' }
                                              },
                                              title: {
                                                text: 'Freight Spend',
                                                style: {
                                                  fontSize: '12px',
                                                  fontWeight: 'bold'
                                                }
                                              }
                                            },
                                            yaxis: {
                                              lines: {
                                                show: false // Hide horizontal grid lines
                                              },
                                              categories: [
                                                '0-49',
                                                '50-99',
                                                '100-249',
                                                '250-499',
                                                '500-749',
                                                '750-999',
                                                '1,000-1,499',
                                                '1,500-1,999',
                                                '2,000-2,499',
                                                '2,500+'
                                              ],
                                              labels: {
                                                show: true,
                                                minWidth: 80,
                                                maxWidth: 100,
                                                style: {
                                                  fontSize: '11px',
                                                  fontWeight: 'bold'
                                                }
                                              }
                                            },
                                            grid: {
                                              padding: { left: 5, right: 5 }
                                            },
                                            colors: [
                                              '#008FFB',
                                              '#00E396',
                                              '#FEB019',
                                              '#FF4560',
                                              '#775DD0',
                                              '#3F51B5',
                                              '#546E7A',
                                              '#D4526E',
                                              '#8D5B4C'
                                            ],
                                            tooltip: {
                                              y: {
                                                formatter: function (
                                                  val,
                                                  { dataPointIndex, w }
                                                ) {
                                                  const mileRanges = [
                                                    '0-49 miles',
                                                    '50-99 miles',
                                                    '100-249 miles',
                                                    '250-499 miles',
                                                    '500-749 miles',
                                                    '750-999 miles',
                                                    '1,000-1,499 miles',
                                                    '1,500-1,999 miles',
                                                    '2,000-2,499 miles',
                                                    '2,500+ miles'
                                                  ];
                                                  const mileRange =
                                                    mileRanges[dataPointIndex];
                                                  return `${mileRange}: $${val.toLocaleString()}`;
                                                }
                                              }
                                            },
                                            labels: [
                                              '0-49',
                                              '50-99',
                                              '100-249',
                                              '250-499',
                                              '500-749',
                                              '750-999',
                                              '1,000-1,499',
                                              '1,500-1,999',
                                              '2,000-2,499',
                                              '2,500+'
                                            ]
                                          }}
                                          series={[
                                            {
                                              name: 'Freight Spend',
                                              data: freightByMilesData
                                            }
                                          ]}
                                          type='bar'
                                          height={280}
                                        />
                                      </Box>
                                    </ChartContainer>
                                  </Grid>

                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Freight Spend by Shipment Type
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('shipmentTypePie')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={shipTypePieOptionsWithClick}
                                        series={shipTypePieSeries}
                                        type='donut'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <BubbleMaps
                                  shipments={shipments}
                                  setExpandedChart={setExpandedChart}
                                  expandedChart={expandedChart}
                                  tabValue={activeTab}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {/* # of Shipments Tab */}
                        {currentTab.key === 'shipments' && (
                          <Box>
                            {/* Stats Cards for Shipments */}
                            <Grid container spacing={0.5} sx={{ mb: 1 }}>
                              <Grid item xs={12} md={9}>
                                <Grid container spacing={0.5}>
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Grid
                                        container
                                        spacing={0.5}
                                        sx={{
                                          mb: 1
                                        }}
                                      >
                                        {statCards.map((card, index) => (
                                          <Grid item xs={12} sm={6} key={index}>
                                            <StatCard color={card.color}>
                                              <CardContent
                                                sx={{
                                                  p: 1,
                                                  '&:last-child': { pb: 1 },
                                                  height: '20px'
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                      'space-between',
                                                    alignItems: 'center'
                                                  }}
                                                >
                                                  <Box>
                                                    <Tooltip
                                                      title={card.tooltip}
                                                      arrow
                                                      placement='top'
                                                    >
                                                      <Typography
                                                        variant='h4'
                                                        component='div'
                                                        fontWeight='bold'
                                                        sx={{
                                                          cursor: 'pointer'
                                                        }}
                                                      >
                                                        {card.value}
                                                      </Typography>
                                                    </Tooltip>
                                                    <Typography
                                                      variant='body2'
                                                      sx={{
                                                        opacity: 0.9,
                                                        fontSize: '0.75rem'
                                                      }}
                                                    >
                                                      {card.title}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              </CardContent>
                                            </StatCard>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </ChartContainer>
                                  </Grid>

                                  {/* Monthly Shipment Count */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Monthly Shipment Count
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('monthlyShipments')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  monthlyShipmentCountData
                                                    .categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              if (val >= 1000) {
                                                return (
                                                  (val / 1000).toFixed(1) + 'k'
                                                );
                                              }
                                              return val;
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              monthlyShipmentCountData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            tickAmount: 6,
                                            labels: {
                                              formatter: function (val) {
                                                if (val >= 1000) {
                                                  return (
                                                    (val / 1000).toFixed(1) +
                                                    'k'
                                                  );
                                                }
                                                return val;
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: ['#00E396'],
                                          tooltip: {
                                            y: {
                                              formatter: function (val) {
                                                return (
                                                  val.toLocaleString() +
                                                  ' shipments'
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={[
                                          {
                                            name: 'Shipment Count',
                                            data: monthlyShipmentCountData.data
                                          }
                                        ]}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>

                                  {/* Monthly Shipments by Type */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Monthly Shipments by Type
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart(
                                              'shipmentTypeMonthlyCount'
                                            )
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  monthlyShipmentTypeCountData
                                                    .categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              if (val >= 1000) {
                                                return (
                                                  (val / 1000).toFixed(1) + 'k'
                                                );
                                              }
                                              return val > 0 ? val : '';
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              monthlyShipmentTypeCountData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            labels: {
                                              formatter: function (val) {
                                                if (val >= 1000) {
                                                  return (
                                                    (val / 1000).toFixed(1) +
                                                    'k'
                                                  );
                                                }
                                                return val > 0 ? val : '';
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: [
                                            '#008FFB',
                                            '#00E396',
                                            '#FF4560'
                                          ],
                                          legend: {
                                            position: 'top',
                                            fontSize: '12px'
                                          },
                                          tooltip: {
                                            y: {
                                              formatter: function (
                                                val,
                                                { seriesIndex }
                                              ) {
                                                const type = [
                                                  'LTL',
                                                  'Parcel',
                                                  'TL'
                                                ][seriesIndex];
                                                return (
                                                  type +
                                                  ': ' +
                                                  val.toLocaleString() +
                                                  ' shipments'
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={
                                          monthlyShipmentTypeCountData.series
                                        }
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>
                                  {/* Shipments by Type - Donut Chart */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          # of Shipments by Shipments Type
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart(
                                              'shipmentsByTypeDonut'
                                            )
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'donut',
                                            height: 250,
                                            events: {
                                              dataPointSelection:
                                                handleDonutClick
                                            }
                                          },
                                          plotOptions: {
                                            pie: {
                                              dataLabels: {
                                                offset: 20,
                                                minAngleToShowLabel: 0
                                              },
                                              donut: {
                                                size: '65%',
                                                labels: {
                                                  show: true,
                                                  name: {
                                                    show: true,
                                                    fontSize: '12px',
                                                    fontWeight: 'bold'
                                                  },
                                                  value: {
                                                    show: true,
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    formatter: function (val) {
                                                      return val >= 1000
                                                        ? (val / 1000).toFixed(
                                                            1
                                                          ) + 'k'
                                                        : val.toLocaleString();
                                                    }
                                                  }
                                                }
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            style: {
                                              fontSize: '11px',
                                              fontWeight: 'bold',
                                              colors: ['#1a237e']
                                            },
                                            background: {
                                              enabled: true,
                                              foreColor: '#fff',
                                              padding: 4,
                                              borderRadius: 2,
                                              borderWidth: 0,
                                              opacity: 0.9
                                            },
                                            enabled: true,
                                            formatter: function (_val, opts) {
                                              const actualValue =
                                                opts.w.globals.series[
                                                  opts.seriesIndex
                                                ];
                                              return actualValue.toLocaleString();
                                            },

                                            dropShadow: {
                                              enabled: false
                                            }
                                          },
                                          legend: {
                                            position: 'top',
                                            horizontalAlign: 'center',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            markers: {
                                              width: 10,
                                              height: 10,
                                              radius: 5
                                            },
                                            itemMargin: {
                                              horizontal: 0,
                                              vertical: 1
                                            }
                                          },
                                          colors: [
                                            '#008FFB',
                                            '#00E396',
                                            '#FEB019',
                                            '#FF4560',
                                            '#775DD0'
                                          ],
                                          tooltip: {
                                            y: {
                                              formatter: function (val, opts) {
                                                // Show actual count in tooltip
                                                const labels = [
                                                  'Outbound',
                                                  'Inbound',
                                                  'Transfer',
                                                  'Dropship'
                                                ];
                                                const label =
                                                  labels[opts.seriesIndex] ||
                                                  `Type ${
                                                    opts.seriesIndex + 1
                                                  }`;
                                                return `${label}: ${val.toLocaleString()} shipments`;
                                              }
                                            }
                                          },
                                          labels: [
                                            'Outbound',
                                            'Inbound',
                                            'Transfer',
                                            'Dropship'
                                          ],
                                          responsive: [
                                            {
                                              breakpoint: 480,
                                              options: {
                                                chart: {
                                                  width: '100%'
                                                },
                                                legend: {
                                                  position: 'bottom'
                                                }
                                              }
                                            }
                                          ]
                                        }}
                                        series={
                                          shipmentCountsByType
                                            ? Object.values(
                                                shipmentCountsByType
                                              )
                                            : [0, 0, 0, 0]
                                        }
                                        type='donut'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>
                                  {/* Average Shipment Size (lbs) */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Average Shipment Size (lbs)
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('avgShipmentSize')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  avgShipmentSizeData
                                                    .categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              if (val >= 1000) {
                                                return (
                                                  (val / 1000).toFixed(1) + 'k'
                                                );
                                              }
                                              return Math.round(val);
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              avgShipmentSizeData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            labels: {
                                              formatter: function (val) {
                                                if (val >= 1000) {
                                                  return (
                                                    (val / 1000).toFixed(1) +
                                                    'k'
                                                  );
                                                }
                                                return Math.round(val);
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: ['#775DD0'],
                                          tooltip: {
                                            y: {
                                              formatter: function (val) {
                                                return (
                                                  Math.round(
                                                    val
                                                  ).toLocaleString() + ' lbs'
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={[
                                          {
                                            name: 'Avg Shipment Size',
                                            data: avgShipmentSizeData.data
                                          }
                                        ]}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>
                                  {/* Pounds Moved per Month */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Pounds Moved per Month
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart(
                                              'poundsMovedMonthly'
                                            )
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <ReactApexChart
                                        options={{
                                          chart: {
                                            type: 'bar',
                                            height: 220,
                                            toolbar: { show: false },
                                            events: {
                                              dataPointSelection: (
                                                event,
                                                chartContext,
                                                config
                                              ) => {
                                                const selectedMonth =
                                                  poundsMovedMonthlyData
                                                    .categories[
                                                    config.dataPointIndex
                                                  ];
                                                if (selectedMonth) {
                                                  handleMonthFilter(
                                                    selectedMonth
                                                  );
                                                }
                                              }
                                            }
                                          },
                                          plotOptions: {
                                            bar: {
                                              borderRadius: 2,
                                              columnWidth: '50%',
                                              dataLabels: {
                                                position: 'top'
                                              }
                                            }
                                          },
                                          dataLabels: {
                                            enabled: true,
                                            formatter: function (val) {
                                              return (
                                                (val / 1000).toFixed(0) + 'k'
                                              );
                                            },
                                            offsetY: -20,
                                            style: {
                                              fontSize: '9px',
                                              colors: ['#304758'],
                                              fontWeight: 'bold'
                                            }
                                          },
                                          xaxis: {
                                            categories:
                                              poundsMovedMonthlyData.categories,
                                            labels: {
                                              style: {
                                                fontSize: '10px'
                                              },
                                              rotate: -45,
                                              trim: false
                                            }
                                          },
                                          yaxis: {
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            labels: {
                                              formatter: function (val) {
                                                return (
                                                  (val / 1000000).toFixed(1) +
                                                  'M'
                                                );
                                              },
                                              style: {
                                                fontSize: '10px'
                                              }
                                            }
                                          },
                                          grid: {
                                            padding: {
                                              left: 5,
                                              right: 5
                                            }
                                          },
                                          colors: ['#FF4560'],
                                          tooltip: {
                                            y: {
                                              formatter: function (val) {
                                                return (
                                                  val.toLocaleString() + ' lbs'
                                                );
                                              }
                                            }
                                          }
                                        }}
                                        series={[
                                          {
                                            name: 'Pounds Moved',
                                            data: poundsMovedMonthlyData.data
                                          }
                                        ]}
                                        type='bar'
                                        height={220}
                                      />
                                    </ChartContainer>
                                  </Grid>

                                  {/* Top 5 Carries Chart */}

                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Top 5 Carriers
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('topCarriers')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <Box
                                        sx={{
                                          '& .apexcharts-canvas': {
                                            cursor: 'pointer'
                                          },
                                          '& .apexcharts-tooltip': {
                                            zIndex: 1000,
                                            boxShadow:
                                              '0 4px 12px rgba(0,0,0,0.15)',
                                            transform: 'translateY(-120px)'
                                          }
                                        }}
                                      >
                                        <ReactApexChart
                                          options={{
                                            chart: {
                                              type: 'line',
                                              height: 250,
                                              toolbar: { show: false },
                                              events: {
                                                dataPointSelection: (
                                                  event,
                                                  chartContext,
                                                  config
                                                ) => {
                                                  const selectedCarrier =
                                                    topCarriersData
                                                      .carrierNames[
                                                      config.dataPointIndex
                                                    ];
                                                  if (selectedCarrier) {
                                                    handleCarrierFilter(
                                                      selectedCarrier
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                            stroke: {
                                              width: [0, 2] // 0 for bars, 2 for line
                                            },
                                            xaxis: {
                                              categories:
                                                topCarriersData.carrierNames,
                                              labels: {
                                                style: {
                                                  fontSize: '11px',
                                                  fontWeight: 'bold'
                                                },
                                                rotate: -45, // Rotate labels to prevent overlap
                                                hideOverlappingLabels: false, // Force show all labels
                                                trim: false, // Don't trim text
                                                maxHeight: 60 // Allow more height for rotated labels
                                              },
                                              tickPlacement: 'on' // Place ticks on the labels
                                            },
                                            yaxis: [
                                              {
                                                // Left y-axis for pounds (in K)
                                                seriesName: 'Pounds',
                                                labels: {
                                                  formatter: function (val) {
                                                    return (
                                                      (val / 1000).toFixed(0) +
                                                      'K'
                                                    );
                                                  },
                                                  style: {
                                                    fontSize: '10px'
                                                  }
                                                }
                                              },
                                              {
                                                // Right y-axis for shipment count
                                                seriesName: 'Shipments',
                                                opposite: true,
                                                labels: {
                                                  formatter: function (val) {
                                                    return Math.round(
                                                      val
                                                    ).toLocaleString();
                                                  },
                                                  style: {
                                                    fontSize: '10px'
                                                  }
                                                }
                                              }
                                            ],
                                            lines: {
                                              show: false // Hide horizontal grid lines
                                            },
                                            colors: ['#008FFB', '#FF4560'], // Blue for bars, Red for line
                                            fill: {
                                              opacity: [0.85, 1]
                                            },
                                            grid: {
                                              padding: {
                                                bottom: 40 // Add padding at bottom for rotated labels
                                              }
                                            },
                                            tooltip: {
                                              shared: true,
                                              intersect: false,
                                              y: {
                                                formatter: function (
                                                  val,
                                                  {
                                                    seriesIndex,
                                                    dataPointIndex
                                                  }
                                                ) {
                                                  const carrierName =
                                                    topCarriersData
                                                      .carrierNames[
                                                      dataPointIndex
                                                    ];
                                                  if (seriesIndex === 0) {
                                                    return `${carrierName}<br/>Pounds: ${val.toLocaleString()}`;
                                                  }
                                                  if (seriesIndex === 1) {
                                                    return `${carrierName}<br/>Shipments: ${val.toLocaleString()}`;
                                                  }
                                                  return val;
                                                }
                                              }
                                            },
                                            legend: {
                                              position: 'top',
                                              horizontalAlign: 'center',
                                              fontSize: '12px',
                                              itemMargin: {
                                                horizontal: 10,
                                                vertical: 5
                                              }
                                            }
                                          }}
                                          series={[
                                            {
                                              name: 'Total Pounds',
                                              type: 'column', // Bars
                                              data: topCarriersData.poundsData
                                            },
                                            {
                                              name: 'Shipments',
                                              type: 'line',
                                              data: topCarriersData.shipmentsData
                                            }
                                          ]}
                                          height={250}
                                        />
                                      </Box>
                                    </ChartContainer>
                                  </Grid>
                                  {/* Top 5 Lanes Chart*/}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Top 5 Lanes
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart('topLanes')
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          width: '100%'
                                        }}
                                      >
                                        {/* Lane Names on Left */}
                                        <Box
                                          sx={{
                                            width: '50%',
                                            minWidth: 170,
                                            pr: 0.5,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            height: 180
                                          }}
                                        >
                                          {topLanesData.displayNames.map(
                                            (lane, index) => (
                                              <Box
                                                key={index}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  height: 30
                                                }}
                                              >
                                                <Typography
                                                  variant='caption'
                                                  sx={{
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    color: '#304758'
                                                  }}
                                                >
                                                  {index + 1}. {lane}
                                                </Typography>
                                              </Box>
                                            )
                                          )}
                                        </Box>

                                        {/* Chart on Right */}
                                        <Box
                                          sx={{
                                            width: '50%',
                                            position: 'relative',
                                            marginLeft: '-10px'
                                          }}
                                        >
                                          <ReactApexChart
                                            options={{
                                              chart: {
                                                type: 'bar',
                                                height: 220,
                                                toolbar: { show: false },
                                                offsetY: 1
                                              },
                                              plotOptions: {
                                                bar: {
                                                  horizontal: true,
                                                  barHeight: '40%',
                                                  borderRadius: 2,
                                                  dataLabels: {
                                                    position: 'right'
                                                  }
                                                }
                                              },
                                              dataLabels: {
                                                enabled: true,
                                                formatter: function (val) {
                                                  return (
                                                    '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0' +
                                                    val.toLocaleString()
                                                  );
                                                },
                                                style: {
                                                  fontSize: '11px',
                                                  colors: ['#304758'],
                                                  fontWeight: 'bold'
                                                }
                                              },
                                              xaxis: {
                                                labels: {
                                                  formatter: function (val) {
                                                    return (
                                                      '\u00A0' +
                                                      val.toLocaleString()
                                                    );
                                                  },
                                                  style: {
                                                    fontSize: '10px'
                                                  }
                                                }
                                              },
                                              yaxis: {
                                                show: false,
                                                labels: {
                                                  show: false
                                                },
                                                axisBorder: {
                                                  show: false
                                                },
                                                axisTicks: {
                                                  show: false
                                                }
                                              },
                                              colors: ['#FEB019'],
                                              tooltip: {
                                                y: {
                                                  formatter: function (
                                                    val,
                                                    { dataPointIndex }
                                                  ) {
                                                    const lane =
                                                      topLanesData.details[
                                                        dataPointIndex
                                                      ];
                                                    return `${
                                                      lane.originCity
                                                    } → ${
                                                      lane.destinationCity
                                                    }<br/>Shipments: ${val.toLocaleString()}`;
                                                  }
                                                }
                                              },
                                              grid: {
                                                padding: {
                                                  left: 0,
                                                  right: 30,
                                                  top: -20,
                                                  bottom: 20
                                                },
                                                yaxis: {
                                                  lines: {
                                                    show: false
                                                  }
                                                }
                                              }
                                            }}
                                            series={[
                                              {
                                                name: 'Shipments',
                                                data: topLanesData.shipmentCounts
                                              }
                                            ]}
                                            type='bar'
                                            height={220}
                                          />
                                        </Box>
                                      </Box>
                                    </ChartContainer>
                                  </Grid>
                                  {/* # of Shipments by Weight lbs chart */}
                                  <Grid item xs={12} md={4}>
                                    <ChartContainer elevation={1}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}
                                      >
                                        <Typography
                                          variant='h6'
                                          fontWeight='bold'
                                          fontSize='1rem'
                                        >
                                          Shipments by Weight (lbs)
                                        </Typography>
                                        <IconButton
                                          size='small'
                                          sx={{
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              backgroundColor:
                                                'rgba(0,0,0,0.08)'
                                            }
                                          }}
                                          onClick={() =>
                                            setExpandedChart(
                                              'shipmentsByWeight'
                                            )
                                          }
                                        >
                                          <OpenInFull fontSize='small' />
                                        </IconButton>
                                      </Box>

                                      <Box
                                        sx={{
                                          overflowX: 'auto',
                                          overflowY: 'visible'
                                        }}
                                      >
                                        <ReactApexChart
                                          options={{
                                            chart: {
                                              type: 'bar',
                                              height: 280,
                                              toolbar: { show: false },
                                              events: {
                                                dataPointSelection: (
                                                  event,
                                                  chartContext,
                                                  config
                                                ) => {
                                                  const selectedWeightRange =
                                                    weightRangeNames[
                                                      config.dataPointIndex
                                                    ];
                                                  if (selectedWeightRange) {
                                                    handleWeightFilter(
                                                      selectedWeightRange
                                                    );
                                                  }
                                                }
                                              }
                                            },
                                            plotOptions: {
                                              bar: {
                                                distributed: true,
                                                horizontal: true,
                                                barHeight: '60%',
                                                dataLabels: {
                                                  position: 'right',
                                                  hideOverflowingLabels: false
                                                }
                                              }
                                            },
                                            dataLabels: {
                                              enabled: true,
                                              formatter: function (val) {
                                                const pad =
                                                  '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                                                return (
                                                  pad + val.toLocaleString()
                                                );
                                              },
                                              style: {
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                colors: ['#304758'],
                                                textShadow: '1px 1px 2px white'
                                              },
                                              background: {
                                                enabled: false
                                              },
                                              offsetX: 20
                                            },
                                            xaxis: {
                                              labels: {
                                                formatter: function (val) {
                                                  return val.toLocaleString();
                                                },
                                                style: { fontSize: '11px' }
                                              }
                                            },
                                            yaxis: {
                                              lines: {
                                                show: false // Hide horizontal grid lines
                                              },
                                              categories: [
                                                '<500',
                                                '500-999',
                                                '1,000-1,999',
                                                '2,000-4,999',
                                                '5,000-9,999',
                                                '10,000-19,999',
                                                '20,000-40,000',
                                                '>40,000'
                                              ],
                                              labels: {
                                                show: true,
                                                minWidth: 80,
                                                maxWidth: 100,
                                                style: {
                                                  fontSize: '11px',
                                                  fontWeight: 'bold'
                                                }
                                              }
                                            },
                                            legend: { show: false },
                                            grid: {
                                              padding: { left: 5, right: 5 }
                                            },
                                            colors: [
                                              '#008FFB',
                                              '#00E396',
                                              '#FEB019',
                                              '#FF4560',
                                              '#775DD0',
                                              '#3F51B5',
                                              '#546E7A',
                                              '#D4526E'
                                            ],
                                            tooltip: {
                                              y: {
                                                formatter: function (
                                                  val,
                                                  { dataPointIndex, w }
                                                ) {
                                                  const weightRanges = [
                                                    'Under 500 lbs',
                                                    '500-749 lbs',
                                                    '750-999 lbs',
                                                    '1,000-1,999 lbs',
                                                    '2,000-4,999 lbs',
                                                    '5,000-9,999 lbs',
                                                    '10,000-19,999 lbs',
                                                    '20,000-40,000 lbs',
                                                    'Over 40,000 lbs'
                                                  ];
                                                  const weightRange =
                                                    weightRanges[
                                                      dataPointIndex
                                                    ];
                                                  return `${weightRange}: ${val.toLocaleString()} shipments`;
                                                }
                                              }
                                            },
                                            labels: [
                                              '<500',
                                              '500-999',
                                              '1,000-1,999',
                                              '2,000-4,999',
                                              '5,000-9,999',
                                              '10,000-19,999',
                                              '20,000-40,000',
                                              '>40,000'
                                            ]
                                          }}
                                          series={[
                                            {
                                              name: 'Shipments',
                                              data: shipmentsByWeightData
                                            }
                                          ]}
                                          type='bar'
                                          height={280}
                                        />
                                      </Box>
                                    </ChartContainer>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <BubbleMaps
                                  shipments={shipments}
                                  setExpandedChart={setExpandedChart}
                                  expandedChart={expandedChart}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {/* Origin Tab */}
                        {currentTab.key === 'origin' && (
                          <OriginDestinationTab
                            shipments={shipments}
                            tabValue={2}
                          />
                        )}

                        {/* Destination Tab */}
                        {currentTab.key === 'destination' && (
                          <OriginDestinationTab
                            shipments={shipments}
                            tabValue={3}
                          />
                        )}

                        {/* On-Time Delivery Tab */}
                        {currentTab.key === 'onTimeDelivery' && (
                          <OnTimeDeliveryTab
                            shipments={shipments.filter(item => {
                              // Basic criteria check
                              const basicCheck =
                                item.actualDeliveryDate &&
                                item.shipmentStatus === 'Delivered' &&
                                item.shipmentTypeRule === 'Outbound' &&
                                item.shipmentType === 'LTL';

                              if (!basicCheck) return false;

                              // FIXED: Use consistent EST timezone for date range check
                              if (filters?.startDate && filters?.endDate) {
                                return isDateInRangeEST(
                                  item.actualDeliveryDate,
                                  filters.startDate,
                                  filters.endDate
                                );
                              }

                              return true;
                            })}
                            lateShipsCol={
                              userPermissionsArr.filter(
                                permissions => permissions.level6
                              ).length || UserIsAdmin
                            }
                            viewDetails={
                              userPermissionsArr.filter(
                                permissions => permissions.level7
                              ).length || UserIsAdmin
                            }
                          />
                        )}

                        {/* Weekly Trend Tab */}
                        {currentTab.key === 'weeklyTrend' && (
                          <WeeklyTrendsTab shipments={shipments} />
                        )}

                        {/* Claims Tab */}
                        {currentTab.key === 'claims' && (
                          <ClaimsDashboard shipments={shipments} />
                        )}
                      </>
                    )}
                  </Box>
                </>
              );
            })()}
          </>
        )}
      </DashboardContainer>
    </>
  );
};

export default TMSDashboard;
